import {
	createAsyncThunk,
	createSlice,
	nanoid,
	type PayloadAction,
} from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { cloneDeep, isEqual, omit, orderBy, pick } from 'lodash'
import type { CompanyQuoteTemplate, QuoteJsonInputDetails } from 'src/API'
import getRecapGridTotals from 'src/components/ui/Grid/PrepGrid/GridFunctions/GridUpdate/getRecapGridTotals'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { projectApi } from 'src/redux/api/graphql/projectsApi'
import { quoteApi } from 'src/redux/api/graphql/quoteApi'
import { quoteInfoApi } from 'src/redux/api/graphql/quoteInfoApi'
import { recapInfoApi } from 'src/redux/api/graphql/recapInfoApi'
import { setFetchProjectApiStatus } from 'src/redux/slices/projects'
import { updatePurchase } from 'src/redux/slices/purchases'
import type { AppDispatch, RootState, ThunkAppDispatch } from 'src/redux/store'
import type {
	CreateQuoteMutationApi,
	CreateRecapInfoMutationApi,
	GetCompanyQueryApi,
	GetProjectsQueryApi,
	GetQuoteInfoQueryApi,
	GetQuoteQueryApi,
	ReduxClients,
	ReduxCompany,
	ReduxCompanyQuoteTemplate,
	ReduxInvoices,
	ReduxProjects,
	ReduxPurchase,
	ReduxQuote,
	ReduxQuoteInfo,
	ReduxQuoteInfoNewRow,
	ReduxRecapInfo,
	ReduxRecapInfoNewRow,
	ReduxSectionRename,
	ReduxUserInfo,
	UpdateQuoteInfoMutationApi,
	UpdateQuoteMutationApi,
	UpdateRecapInfoMutationApi,
} from 'src/types/apiType'
import type { QuoteType, UpdateQuoteAttributes } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import {
	checkIfItemIsAccessible,
	checkIfTemplateIsAccessible,
	fetchAllQuotesByTenantIdUtilForSubTeamAdmin,
	fetchAllQuotesByTenantIdUtilForTeamAdmin,
	fetchAllQuotesByTenantIdUtilNew,
	fetchChildFreelancerProjectUtil,
	fetchChildFreelancerQuotes,
	fetchClientsUtil,
	fetchProjectsByTenantIdUtil,
	fetchProjectsByTenantIdUtilForSubTeamAdmin,
	fetchProjectsByTenantIdUtilForTeamAdmin,
	fetchQuoteInfoUtil,
	fetchQuotesByParentProjectIdUtil,
	fetchRecapInfoUtil,
	fetchSectionRenameUtil,
	getCostlineFetchFilter,
	getFetchFilter,
} from 'src/utils/fetchUtils'
import { makeErrorToast, makeSuccessToast, msg } from 'src/utils/makeToast'
import {
	checkNewObjectKeysPresent,
	checkNumber,
	cleanObject,
	delayInMs,
	getQuoteNumberNew,
	getQuoteOrProjectNumberDetails,
	updateCompanyInfoWithDeltaSyncSuppress,
	zeroPad,
} from 'src/utils/miscellaneous'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import { sanitizeQuoteProductionNotes } from 'src/utils/productionNotesRichText'
import { getQuoteVersion } from 'src/utils/quote/quoteUtil'
import {
	createCostLine,
	createCostLineIdsAndLabels,
} from 'src/utils/quoteGridUtils/createQuoteUtil'
import { getCreateQuoteRecapInfoUtil } from 'src/utils/recapGridUtils/createQuoteRecapInfoUtil/getCreateQuoteRecapInfoUtil'
import { isRecapConditionalCheckError } from 'src/utils/recapGridUtils/isRecapConditionalCheckError'
import {
	getProperUpdateValues,
	hasBusinessChanges,
} from 'src/utils/updateApiUtil'

type ApiStatus = 'idle' | 'loading' | 'succeeded' | 'failed' | 'rejected'

interface QuoteState {
	allQuotes: UpdateQuoteAttributes[]
	currentQuote: ReduxQuote | null
	currentQuoteInfo: ReduxRecapInfo | null
	oldQuoteInfo: ReduxQuoteInfo[]
	quoteInfo: ReduxQuoteInfo[]
	oldRecapQuoteInfo: ReduxRecapInfo[]
	recapQuoteInfo: ReduxRecapInfo[]
	singleRecapInfo: ReduxRecapInfo | null
	sectionRenameDict: Record<string, ReduxSectionRename>
	listMergedQuotesData: ReduxQuote[]
	calculateRecapInfoStatus: boolean
	createOrUpdateQuoteApiStatus: ApiStatus
	updateOfflineIdsStatus: ApiStatus
	deleteQuoteApiStatus: ApiStatus
	fetchAllQuotesApiStatus: ApiStatus
	fetchQuoteApiStatus: ApiStatus
	fetchQuoteInfoApiStatus: ApiStatus
	updateQuoteInfoApiStatus: ApiStatus
	fetchRecapQuoteInfoApiStatus: ApiStatus
	refreshRecapQuoteInfoApiStatus: ApiStatus
	updateRecapQuoteInfoApiStatus: ApiStatus
	duplicateQuoteApiStatus: ApiStatus
	moveQuoteApiStatus: ApiStatus
	uploadQuoteApiStatus: ApiStatus
	removeSupplierFromPrepApiStatus: ApiStatus
	assignUserToQuoteApiStatus: ApiStatus
	removeAssignUserToQuoteApiStatus: ApiStatus
	toggleSetQuoteExpiryApiStatus: ApiStatus
	addOrRemoveSmgQuoteApiStatus: ApiStatus
	updateQuoteInfoDebounceApiStatus: ApiStatus
	updateRecapQuoteInfoDebounceApiStatus: ApiStatus
	mergeQuoteApiStatus: ApiStatus
	listMergeQuoteApiStatus: ApiStatus
	addSecondaryQuoteApiStatus: ApiStatus
	detachMemberQuoteApiStatus: ApiStatus
	updateCostLineLabelApiStatus: ApiStatus
	calculateMarginsAndTotalsApiStatus: ApiStatus
	updateQuoteInfoApiCounter: number
	updateRecapQuoteInfoApiCounter: number
	addQuoteModalOpenStatus: boolean
	addQuoteJsonModalOpenStatus: boolean
	addQuoteFilledCostlineModalOpenStatus: boolean
	quoteDeleteDialogOpenStatus: boolean
	quoteSummaryTotalsModalOpenStatus: boolean
	quoteUpdateDiscountModalOpenStatus: boolean
	quoteUpdateCustomizeModalOpenStatus: boolean
	quoteShareModalOpenStatus: boolean
	editCo2EmissionSectionModalOpenStatus: boolean
	editMarginLabelModalOpenStatus: boolean
	recapSummaryTotalsModalOpenStatus: boolean
	scrollToCategoryModalOpenStatus: boolean
	quotePdfGeneratorModalOpenStatus: boolean
	recapQuotePdfModalOpenStatus: boolean
	quoteDuplicateDialogOpenStatus: boolean
	quoteMoveDialogOpenStatus: boolean
	ecoProdDialogOpenStatus: boolean
	removeSupplierFromPrepConfirmDialogStatus: boolean
	removeDateRangeFromRecapConfirmDialogStatus: boolean
	quoteSmgDialogStatus: { isOpen: boolean; section?: string | null }
	quoteEditModalStatus: { isOpen: boolean; section?: string | null }
	mergeQuoteModalOpenStatus: boolean
	addSecondaryQuoteModalOpenStatus: boolean
	costLineLabelSyncDialogOpenStatus: boolean
	syncMealDialogOpenStatus: boolean
	summaryFetchQuoteStatus: ApiStatus
	scrollToCategory: string | null
	maximizeMinimize: 'maximize' | 'minimize'
	expandedRows: any
	filters: {
		item: string
		item_fr: string
	}
	columnVisibility: Record<string, boolean>
	prepColumnVisibility: Record<string, boolean>
	wrapColumnVisibility: Record<string, boolean>
	smgMenuAnchorEl: any
	editQuoteMenuAnchorEl: any
	error?: string | null
	quoteJsonDetails: QuoteJsonInputDetails | null
	showOtColumns: boolean
}

const initialState: QuoteState = {
	allQuotes: [],
	currentQuote: null,
	currentQuoteInfo: null,
	oldQuoteInfo: [],
	quoteInfo: [],
	oldRecapQuoteInfo: [],
	recapQuoteInfo: [],
	singleRecapInfo: null,
	sectionRenameDict: {},
	listMergedQuotesData: [],
	calculateRecapInfoStatus: false,
	createOrUpdateQuoteApiStatus: 'idle',
	updateOfflineIdsStatus: 'idle',
	deleteQuoteApiStatus: 'idle',
	fetchAllQuotesApiStatus: 'idle',
	fetchQuoteApiStatus: 'idle',
	fetchQuoteInfoApiStatus: 'idle',
	updateQuoteInfoApiStatus: 'idle',
	fetchRecapQuoteInfoApiStatus: 'idle',
	refreshRecapQuoteInfoApiStatus: 'idle',
	updateRecapQuoteInfoApiStatus: 'idle',
	duplicateQuoteApiStatus: 'idle',
	moveQuoteApiStatus: 'idle',
	uploadQuoteApiStatus: 'idle',
	removeSupplierFromPrepApiStatus: 'idle',
	assignUserToQuoteApiStatus: 'idle',
	removeAssignUserToQuoteApiStatus: 'idle',
	toggleSetQuoteExpiryApiStatus: 'idle',
	addOrRemoveSmgQuoteApiStatus: 'idle',
	updateQuoteInfoDebounceApiStatus: 'idle',
	updateRecapQuoteInfoDebounceApiStatus: 'idle',
	mergeQuoteApiStatus: 'idle',
	listMergeQuoteApiStatus: 'idle',
	addSecondaryQuoteApiStatus: 'idle',
	detachMemberQuoteApiStatus: 'idle',
	updateCostLineLabelApiStatus: 'idle',
	calculateMarginsAndTotalsApiStatus: 'idle',
	updateQuoteInfoApiCounter: 0,
	updateRecapQuoteInfoApiCounter: 0,
	addQuoteModalOpenStatus: false,
	addQuoteJsonModalOpenStatus: false,
	addQuoteFilledCostlineModalOpenStatus: false,
	quoteDeleteDialogOpenStatus: false,
	quoteSummaryTotalsModalOpenStatus: false,
	quoteUpdateDiscountModalOpenStatus: false,
	quoteUpdateCustomizeModalOpenStatus: false,
	quoteShareModalOpenStatus: false,
	editCo2EmissionSectionModalOpenStatus: false,
	editMarginLabelModalOpenStatus: false,
	recapSummaryTotalsModalOpenStatus: false,
	scrollToCategoryModalOpenStatus: false,
	quotePdfGeneratorModalOpenStatus: false,
	recapQuotePdfModalOpenStatus: false,
	quoteDuplicateDialogOpenStatus: false,
	quoteMoveDialogOpenStatus: false,
	ecoProdDialogOpenStatus: false,
	removeSupplierFromPrepConfirmDialogStatus: false,
	removeDateRangeFromRecapConfirmDialogStatus: false,
	quoteSmgDialogStatus: { isOpen: false, section: null },
	quoteEditModalStatus: { isOpen: false },
	mergeQuoteModalOpenStatus: false,
	addSecondaryQuoteModalOpenStatus: false,
	costLineLabelSyncDialogOpenStatus: false,
	syncMealDialogOpenStatus: false,
	summaryFetchQuoteStatus: 'idle',
	scrollToCategory: null,
	maximizeMinimize: 'maximize',
	expandedRows: {},
	filters: {
		item: '',
		item_fr: '',
	},
	columnVisibility: {},
	prepColumnVisibility: {},
	wrapColumnVisibility: {},
	smgMenuAnchorEl: null,
	editQuoteMenuAnchorEl: null,
	error: null,
	quoteJsonDetails: null,
	showOtColumns: false,
}

const throwQuotesError = (
	message: string,
	context: Record<string, unknown> = {}
): never => {
	return captureExceptionAndThrow(new Error(message), {
		location: 'quotesSlice',
		...context,
	})
}

const throwQuotesStatusError = (
	status: string,
	context: Record<string, unknown> = {}
): never => {
	return throwQuotesError(status || 'Unknown quotes error', {
		status,
		...context,
	})
}

export const fetchAllQuotes = createAsyncThunk<
	UpdateQuoteAttributes[],
	{ startDate?: any; endDate?: any },
	{
		state: RootState
		rejectValue: string
	}
>(
	'quote/fetchAllQuotes',
	async ({ startDate, endDate }, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const tenantId = userTableInfo.currentCompanyTenantId
			const isTeamAdmin =
				userTableInfo.cognitoGroup?.includes('teamAdmin')
			const isSubTeamAdmin =
				userTableInfo.cognitoGroup?.includes('subTeamAdmin')
			const isFreelancer =
				userTableInfo.cognitoGroup?.includes('freelancer')
			const teamIds = userTableInfo.teamIds
			const subTeamIds = userTableInfo.subTeamIds
			const freelancerTenantId = userTableInfo.tenantId

			let quoteData = [] as any[]
			let projData = [] as ReduxProjects[]

			if (isTeamAdmin) {
				quoteData = await fetchAllQuotesByTenantIdUtilForTeamAdmin({
					tenantId,
					teamIds,
					startDate,
					endDate,
				})
				projData = await fetchProjectsByTenantIdUtilForTeamAdmin({
					tenantId,
					teamIds,
				})
			} else if (isSubTeamAdmin) {
				quoteData = await fetchAllQuotesByTenantIdUtilForSubTeamAdmin({
					tenantId,
					subTeamIds,
					startDate,
					endDate,
				})
				projData = await fetchProjectsByTenantIdUtilForSubTeamAdmin({
					tenantId,
					subTeamIds,
				})
			} else if (isFreelancer) {
				quoteData = await fetchChildFreelancerQuotes({
					tenantId,
					freelancerTenantId,
				})
				projData = await fetchChildFreelancerProjectUtil({
					freelancerTenantId,
					companyTenantId: tenantId,
				})
			} else {
				quoteData = await fetchAllQuotesByTenantIdUtilNew(
					tenantId,
					startDate,
					endDate
				)
				projData = await fetchProjectsByTenantIdUtil(
					userTableInfo.currentCompanyTenantId
				)
			}

			const {
				data: {
					companyQuoteTemplateByTenantId: {
						items: companyQuoteTemplateData,
					},
				},
			} = (await API.graphql(
				graphqlOperation(queries.companyQuoteTemplateByTenantId, {
					tenantId,
					filter: {
						_deleted: {
							attributeExists: false,
						},
					},
				})
			)) as {
				data: {
					companyQuoteTemplateByTenantId: {
						items: CompanyQuoteTemplate[]
					}
				}
			}
			const filteredQuoteData = quoteData.filter(
				(q) => !companyQuoteTemplateData.some((t) => t.quoteId === q.id)
			)
			let clientsData = [] as ReduxClients[]
			if (!isFreelancer) {
				clientsData = await fetchClientsUtil(
					userTableInfo.currentCompanyTenantId
				)
			}
			const newQuoteData = filteredQuoteData.map((q) => {
				const currProj = projData.find(
					(proj) => proj?.id === q.parentProjectId
				)
				const proposalsList = q?.proposals?.items?.[0]
				const invoicesList = q?.proposals?.items?.[0]?.invoices?.items
				const quoteType1 = [
					'frenchApfp',
					'frenchPostProd',
					'quoteAnimationOnly',
					'quoteAnimationAndLiveAction',
					'360Template',
				]
				let clientNameLocation
				let contactNameLocation
				if (quoteType1.includes(q?.quoteType)) {
					clientNameLocation = 'quoteHeaderValue1'
					contactNameLocation = 'quoteHeaderValue5'
				} else {
					clientNameLocation = 'quoteHeaderValue2'
					contactNameLocation = 'quoteHeaderValue6'
				}
				q.invoicingStatus = 'Draft'
				if (proposalsList?.status) {
					q.invoicingStatus = proposalsList?.status
				}
				if (q?.isExpired) {
					q.invoicingStatus = 'Expired'
				}

				if (q?.isFinalized) {
					q.budgetStatus = 'Complete'
				} else {
					if (!q?.deliveryDate || dayjs() < dayjs(q?.deliveryDate)) {
						q.budgetStatus = 'On Track'
					} else {
						q.budgetStatus = 'Off Track'
					}
				}
				if (q?.isExpired) {
					q.budgetStatus = 'Lost'
				}

				q.netAmount = q.proposals?.items?.[0]?.total
				q.clientName =
					proposalsList?.clientName ??
					q?.[clientNameLocation as keyof UpdateQuoteAttributes]
				q.contactName =
					q.proposals?.items?.[0]?.contactName ??
					q?.[contactNameLocation as keyof UpdateQuoteAttributes]
				q.projectNumber = currProj?.projectNumber
				q.po = proposalsList?.poName
				q.invoiceNumber = invoicesList
					?.map((inv: ReduxInvoices) => inv?.invoiceNumber)
					.join('\n')
				q.invoiceDate = invoicesList
					?.map((inv: ReduxInvoices) =>
						dayjs(inv?.invoiceDate).format('DD/MM/YYYY')
					)
					.join('\n')

				const teamClient = clientsData?.find(
					(c: ReduxClients) => c.parentTeamId === q.parentTeamId
				)
				if (teamClient) {
					q.teamName = teamClient?.name ?? ''
				}
				const subTeamProject = projData?.find(
					(c: ReduxProjects) =>
						c.parentSubTeamId === q.parentSubTeamId && c.isSubTeam
				)
				if (subTeamProject) {
					q.subTeamName = subTeamProject?.name ?? ''
				}
				return q
			})
			return newQuoteData
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const fetchQuote = createAsyncThunk<
	ReduxQuote,
	{ quoteId: string; page?: string; status?: string },
	{
		state: RootState
		rejectValue: string
	}
>('quote/fetchQuote', async ({ quoteId }, { getState, rejectWithValue }) => {
	try {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const {
			data: { getQuote },
		} = (await API.graphql(
			graphqlOperation(queries.getQuote, { id: quoteId })
		)) as {
			data: {
				getQuote: ReduxQuote | null | undefined
			}
		}
		// I will need to check if the quote is part of companyQuoteTemplate if so
		// i will check against the template instead
		const {
			data: {
				companyQuoteTemplateByQuoteId: {
					items: companyQuoteTemplateData,
				},
			},
		} = (await API.graphql(
			graphqlOperation(queries.companyQuoteTemplateByQuoteId, {
				quoteId,
				filter: {
					_deleted: {
						attributeExists: false,
					},
				},
			})
		)) as {
			data: {
				companyQuoteTemplateByQuoteId: {
					items: ReduxCompanyQuoteTemplate[]
				}
			}
		}
		let accessibleQuote: ReduxQuote | null = null

		if (companyQuoteTemplateData?.length > 0) {
			const templateQuote = companyQuoteTemplateData[0]
			const accessible = checkIfTemplateIsAccessible({
				userInfo: userTableInfo,
				item: templateQuote,
				subTeamIds: [],
			})
			if (accessible && getQuote) {
				accessibleQuote = getQuote
			}
		} else {
			accessibleQuote = checkIfItemIsAccessible(userTableInfo, getQuote)
		}
		if (accessibleQuote) {
			return accessibleQuote as ReduxQuote
		}

		makeErrorToast(msg)
		return rejectWithValue('Error Fetching Quote')
	} catch (err: any) {
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

export const fetchQuoteInfo = createAsyncThunk<
	{
		quoteInfo: ReduxQuoteInfo[]
		sectionRenameDict: Record<string, ReduxSectionRename>
	},
	{ quoteId: string; page?: string },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/fetchQuoteInfo',
	async ({ quoteId }, { dispatch, getState, rejectWithValue }) => {
		// Filter can be used along with sort as another parameter to datastore query
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const quoteFilter = getCostlineFetchFilter(userTableInfo)
			const quoteInfoArray: ReduxQuoteInfo[] = await fetchQuoteInfoUtil(
				quoteId,
				quoteFilter
			)
			const sectionRenameArray: ReduxSectionRename[] =
				await fetchSectionRenameUtil(quoteId)
			const sectionRenameDict = Object.fromEntries(
				sectionRenameArray.map((i) => [i.sectionId, i])
			)
			const noFractionalIndex = quoteInfoArray.find(
				(item) => !item.fractionalSortKey
			)
			if (noFractionalIndex && quoteInfoArray?.length > 0) {
				console.log('noFractionalSort Key: ', noFractionalIndex)
				dispatch(addFractionalSortKeyToQuote(quoteId))
				return {
					quoteInfo: orderBy(
						quoteInfoArray,
						['tableRowIndex'],
						['asc']
					),
					sectionRenameDict,
				}
			}
			const sortedByFractionalSortKey = orderBy(
				quoteInfoArray,
				['fractionalSortKey'],
				['asc']
			)

			return {
				quoteInfo: sortedByFractionalSortKey,
				sectionRenameDict,
			}
		} catch (err) {
			console.error('err: ', err)
			const errMsg = getErrorMessage(err)
			makeErrorToast(msg)
			return rejectWithValue(errMsg)
		}
	}
)

export const fetchRecapQuoteInfo = createAsyncThunk<
	ReduxRecapInfo[] | null,
	{ quoteId: string; calculateRecapInfo?: boolean },
	{
		state: RootState
		rejectValue: string
	}
>(
	'quote/fetchRecapQuoteInfo',
	async ({ quoteId, calculateRecapInfo }, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const quoteFilter = getCostlineFetchFilter(userTableInfo)
			const {
				data: { getQuote: parsedQuoteData },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteId })
			)) as {
				data: { getQuote: ReduxQuote }
			}

			const accessibleQuoteData = checkIfItemIsAccessible(
				userTableInfo,
				parsedQuoteData
			) as ReduxQuote | null
			if (!accessibleQuoteData || !accessibleQuoteData?.quoteType) {
				return rejectWithValue(`Quote can't be accessed`)
			}
			const { createRecapInfo } = getCreateQuoteRecapInfoUtil(
				accessibleQuoteData.quoteType
			)

			const recapInfoCostlines: ReduxRecapInfo[] =
				await fetchRecapInfoUtil(quoteId, quoteFilter)

			const sortedRecapInfo = orderBy(
				recapInfoCostlines,
				['fractionalSortKey'],
				['asc']
			)
			let recapInfoArray: ReduxRecapInfo[] | null
			if (sortedRecapInfo.length === 0 && calculateRecapInfo) {
				const quoteInfoArray: ReduxQuoteInfo[] =
					await fetchQuoteInfoUtil(quoteId, quoteFilter)
				const sortedQuotes = orderBy(
					quoteInfoArray,
					['fractionalSortKey'],
					['asc']
				)

				const tenantId = accessibleQuoteData?.tenantId as string
				recapInfoArray = createRecapInfo(
					sortedQuotes,
					quoteId,
					tenantId,
					accessibleQuoteData?.childFreelancers as string[]
				)
				try {
					// Saves all the recap costline in the db one by one
					const items = recapInfoArray?.map(async (item) => {
						delete item.referenceCostLineUuid
						try {
							await API.graphql(
								graphqlOperation(mutations.createRecapInfo, {
									input: item,
								})
							)
						} catch (createErr) {
							// Deterministic recap ids make a collision the
							// expected sign that another creator (e.g. the
							// masterQuote sync) already created this row. Swallow
							// it so the rollback-by-wipe catch below only fires on
							// genuine errors — otherwise a benign race would
							// delete every recap row for the quote.
							if (!isRecapConditionalCheckError(createErr))
								throw createErr
						}
					})
					// Checks if the items are not undefined
					items && (await Promise.all(items))
				} catch (err: any) {
					//If creating fails for even one recapInfo costline we need to delete the other recap Info costlines already created
					const recapInfoCostlines = await fetchRecapInfoUtil(
						quoteId,
						quoteFilter
					)

					recapInfoCostlines.map(async (item) => {
						await API.graphql(
							graphqlOperation(mutations.deleteRecapInfo),
							{
								input: item.id,
							}
						)
					})
					makeErrorToast(msg)
				}
				return recapInfoArray
			}
			return sortedRecapInfo
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const createQuote = createAsyncThunk<
	string | undefined,
	{
		parentProjectId: string | null
		quoteDetails: Partial<ReduxQuote>
		quoteType: QuoteType
	},
	{
		dispatch: AppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/createQuote',
	async (
		{ parentProjectId = null, quoteDetails, quoteType },
		{ dispatch, getState, rejectWithValue }
	) => {
		try {
			const { auth, projects, settings } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const existingData = projects.projectChildrenData as (
				| ReduxProjects
				| ReduxQuote
			)[]
			const { budgetSections, ...rest } = quoteDetails
			const sort = settings.sort

			const filter = getFetchFilter(userTableInfo)

			const tenantId = userTableInfo.currentCompanyTenantId
			const {
				data: { getCompany: oldCompanyInfoDetails },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, { id: tenantId })
			)) as {
				data: GetCompanyQueryApi
			}

			const {
				data: { getProjects: parsedParentProjectData },
			} = (await API.graphql(
				graphqlOperation(queries.getProjects, {
					id: parentProjectId,
					filter,
				})
			)) as {
				data: GetProjectsQueryApi
			}

			const quoteData = await fetchQuotesByParentProjectIdUtil(
				parentProjectId,
				filter
			)

			if (quoteData.some((e) => e.name === quoteDetails.name)) {
				makeErrorToast('Quote Name already exists!')
				return rejectWithValue('Quote Name already exists!')
			}
			const sanitizedQuoteDetails =
				sanitizeQuoteProductionNotes(quoteDetails)
			// biome-ignore lint/complexity/noForEach: <>
			Object.entries(sanitizedQuoteDetails).forEach(([key, val]) => {
				if (typeof val === 'string') {
					val = val.replaceAll('constructor ', ' Constructor ')
					val = val.replaceAll(' constructor ', ' Constructor ')
					// @ts-expect-error
					sanitizedQuoteDetails[key] = val
				}
			})
			const { companyCount, newTypeCount, newTypeYear } =
				getQuoteOrProjectNumberDetails({
					// @ts-expect-error
					companyCount:
						oldCompanyInfoDetails?.quoteCount &&
						oldCompanyInfoDetails?.quoteCount.length > 0 &&
						oldCompanyInfoDetails?.quoteCount[0] !== null
							? oldCompanyInfoDetails?.quoteCount
							: [],
					type: quoteType,
				})
			const quoteNumber = getQuoteNumberNew({
				newTypeCount,
				newTypeYear,
				type: quoteType,
				useOneQuoteNumber:
					oldCompanyInfoDetails?.useOneQuoteNumber ?? false,
				commonQuoteNumber:
					oldCompanyInfoDetails?.commonQuoteNumber ?? 0,
				// @ts-expect-error
				quoteDetails:
					oldCompanyInfoDetails?.quoteCount &&
					oldCompanyInfoDetails?.quoteCount.length > 0 &&
					oldCompanyInfoDetails?.quoteCount[0] !== null
						? oldCompanyInfoDetails?.quoteCount
						: [],
				quoteCustomisationDetails:
					oldCompanyInfoDetails?.quoteNumberCustomisation &&
					oldCompanyInfoDetails?.quoteNumberCustomisation.length >
						0 &&
					oldCompanyInfoDetails?.quoteNumberCustomisation[0] !== null
						? oldCompanyInfoDetails?.quoteNumberCustomisation
						: [],
			})

			// * We have spread the quote details early because of TS if this causes an issue later check it
			const updatedQuoteDetails: Partial<ReduxQuote> = {
				...sanitizedQuoteDetails,
				id: nanoid(),
				tenantId: parsedParentProjectData?.tenantId,
				activeFlag: true,
				type: 'quote',
				parentTeamId: parsedParentProjectData?.parentTeamId,
				parentSubTeamId: parsedParentProjectData?.parentSubTeamId,
				isStarred: false,
				parentProjectId: parentProjectId || undefined,
				freelancers: ['dummyId'],
				childFreelancers: [
					...(parsedParentProjectData?.childFreelancers || [
						'dummyId',
					]),
				],
				offlineIds: ['dummyId'],
				quoteType: quoteType,
				quoteVersion: getQuoteVersion(quoteType),
				quoteNumber: quoteNumber,
				// * These are mandatory fields in the graphql schema so we add a default value to them
				marginPercentage: sanitizedQuoteDetails.marginPercentage ?? 0,
				overheadPercentage:
					sanitizedQuoteDetails.overheadPercentage ?? 0,
				exchangeRate: sanitizedQuoteDetails.exchangeRate ?? 1,
				localOtNumberOfHours:
					sanitizedQuoteDetails.localOtNumberOfHours,
				exeOtNumberOfHours: sanitizedQuoteDetails.exeOtNumberOfHours,
			}
			const cleanQuoteDetails = cleanObject(updatedQuoteDetails)

			// *  Saves the quote first
			const {
				data: { createQuote: newQuote },
			} = (await API.graphql(
				graphqlOperation(mutations.createQuote, {
					input: cleanQuoteDetails,
				})
			)) as { data: CreateQuoteMutationApi }
			// * Saves the quoteInfo next
			// Gets an array of all the costlines
			if (quoteType === 'blankQuote') {
				await API.graphql(
					graphqlOperation(mutations.createBlankQuoteInfoCostlines, {
						input: {
							quoteId: updatedQuoteDetails.id,
							tenantId: updatedQuoteDetails.tenantId,
							allowInternational:
								updatedQuoteDetails.allowInternational,
							exchangeRate: updatedQuoteDetails.exchangeRate,
							marginPercentage:
								updatedQuoteDetails.marginPercentage,
							overheadPercentage:
								updatedQuoteDetails.overheadPercentage,
							localOtNumberOfHours:
								updatedQuoteDetails.localOtNumberOfHours,
							exeOtNumberOfHours:
								updatedQuoteDetails.exeOtNumberOfHours,
						},
						quoteType: quoteType,
					})
				)
			} else {
				await API.graphql(
					graphqlOperation(mutations.createQuoteInfoCostlines, {
						input: {
							quoteId: updatedQuoteDetails.id,
							tenantId: updatedQuoteDetails.tenantId,
							allowInternational:
								updatedQuoteDetails.allowInternational,
							exchangeRate: updatedQuoteDetails.exchangeRate,
							localExchangeRate:
								updatedQuoteDetails.localExchangeRate,
							marginPercentage:
								updatedQuoteDetails.marginPercentage,
							overheadPercentage:
								updatedQuoteDetails.overheadPercentage,
							localOtNumberOfHours:
								updatedQuoteDetails.localOtNumberOfHours,
							exeOtNumberOfHours:
								updatedQuoteDetails.exeOtNumberOfHours,
						},
						quoteType: quoteType,
						budgetSections: budgetSections,
					})
				)
			}

			// We update the parent project to add the projectType
			if (!parsedParentProjectData?.projectType && parentProjectId) {
				await API.graphql(
					graphqlOperation(mutations.updateProjects, {
						input: {
							id: parentProjectId,
							// We keep the parent project type as the blankQuoteType if the quoteType is blankQuote
							projectType:
								quoteType === 'blankQuote'
									? updatedQuoteDetails?.blankQuoteType
									: quoteType,
						},
					})
				)
				// We invalidate the parent project to update the projectType
				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: parentProjectId },
					])
				)
			}

			// We update the company details to account for change in projectCount
			const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
			const updatedCompanyInfoDetails: Partial<ReduxCompany> = {
				id: oldCompanyInfoDetails?.id,
				quoteCount: companyCount,
			}
			if (oldCompanyInfoDetails?.useOneQuoteNumber) {
				updatedCompanyInfoDetails.commonQuoteNumber = Number.parseInt(
					zeroPad(
						(oldCompanyInfoDetails.commonQuoteNumber || 0) + 1,
						4
					)
				)
			}

			// Keeping a small window before project children are fetched because if called immediately datastore doesn't fetch the changes of api mutation
			// Get a list of all parentProjectIds children after saving the new project
			// dispatch(fetchProjectChildren({ parentProjectId: parentProjectId as string }))

			dispatch(
				projectApi.util.invalidateTags([
					{ type: 'Projects', id: `${parentProjectId}-children` },
				])
			)
			makeSuccessToast(tProjectChildren('quote.successMsg.create'))
			// We will not update CompanyInfo if user is freelancer and he is creating a quote within his archive
			if (
				userTableInfo.cognitoGroup?.includes('freelancer') &&
				userTableInfo.tenantId === userTableInfo.currentCompanyTenantId
			) {
				return
			}
			await updateCompanyInfoWithDeltaSyncSuppress(
				updatedCompanyInfoDetails
			)

			return updatedQuoteDetails.id
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const createQuoteFromJson = createAsyncThunk<
	undefined,
	{
		parentProjectId: string | null
		quoteDetails: Partial<ReduxQuote>
		quoteJsonDetails: QuoteJsonInputDetails
		quoteType: QuoteType
	},
	{
		dispatch: AppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/createQuoteFromJson',
	async (
		{ parentProjectId = null, quoteDetails, quoteJsonDetails, quoteType },
		{ dispatch, getState, rejectWithValue }
	) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const { auth, projects, settings } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const existingData = projects.projectChildrenData as (
				| ReduxProjects
				| ReduxQuote
			)[]
			const { budgetSections, ...rest } = quoteDetails
			const sort = settings.sort

			const filter = getFetchFilter(userTableInfo)

			const tenantId = userTableInfo.currentCompanyTenantId
			const {
				data: { getCompany: oldCompanyInfoDetails },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, { id: tenantId })
			)) as {
				data: GetCompanyQueryApi
			}

			const {
				data: { getProjects: parsedParentProjectData },
			} = (await API.graphql(
				graphqlOperation(queries.getProjects, {
					id: parentProjectId,
					filter,
				})
			)) as {
				data: GetProjectsQueryApi
			}

			const quoteData = await fetchQuotesByParentProjectIdUtil(
				parentProjectId,
				filter
			)

			if (quoteData.some((e) => e.name === quoteDetails.name)) {
				makeErrorToast('Quote Name already exists!')
				return rejectWithValue('Quote Name already exists!')
			}
			const sanitizedQuoteDetails =
				sanitizeQuoteProductionNotes(quoteDetails)
			// biome-ignore lint/complexity/noForEach: <>
			Object.entries(sanitizedQuoteDetails).forEach(([key, val]) => {
				if (typeof val === 'string') {
					val = val.replaceAll('constructor ', ' Constructor ')
					val = val.replaceAll(' constructor ', ' Constructor ')
					// @ts-expect-error
					sanitizedQuoteDetails[key] = val
				}
			})
			const { companyCount, newTypeCount, newTypeYear } =
				getQuoteOrProjectNumberDetails({
					// @ts-expect-error
					companyCount:
						oldCompanyInfoDetails?.quoteCount &&
						oldCompanyInfoDetails?.quoteCount.length > 0 &&
						oldCompanyInfoDetails?.quoteCount[0] !== null
							? oldCompanyInfoDetails?.quoteCount
							: [],
					type: quoteType,
				})
			const quoteNumber = getQuoteNumberNew({
				newTypeCount,
				newTypeYear,
				type: quoteType,
				useOneQuoteNumber:
					oldCompanyInfoDetails?.useOneQuoteNumber ?? false,
				commonQuoteNumber:
					oldCompanyInfoDetails?.commonQuoteNumber ?? 0,
				// @ts-expect-error
				quoteDetails:
					oldCompanyInfoDetails?.quoteCount &&
					oldCompanyInfoDetails?.quoteCount.length > 0 &&
					oldCompanyInfoDetails?.quoteCount[0] !== null
						? oldCompanyInfoDetails?.quoteCount
						: [],
				quoteCustomisationDetails:
					oldCompanyInfoDetails?.quoteNumberCustomisation &&
					oldCompanyInfoDetails?.quoteNumberCustomisation.length >
						0 &&
					oldCompanyInfoDetails?.quoteNumberCustomisation[0] !== null
						? oldCompanyInfoDetails?.quoteNumberCustomisation
						: [],
			})

			// * We have spread the quote details early because of TS if this causes an issue later check it
			const updatedQuoteDetails: Partial<ReduxQuote> = {
				...sanitizedQuoteDetails,
				id: nanoid(),
				tenantId: parsedParentProjectData?.tenantId,
				activeFlag: true,
				type: 'quote',
				parentTeamId: parsedParentProjectData?.parentTeamId,
				parentSubTeamId: parsedParentProjectData?.parentSubTeamId,
				isStarred: false,
				parentProjectId: parentProjectId || undefined,
				freelancers: ['dummyId'],
				childFreelancers: [
					...(parsedParentProjectData?.childFreelancers || [
						'dummyId',
					]),
				],
				offlineIds: ['dummyId'],
				quoteType: quoteType,
				quoteVersion: getQuoteVersion(quoteType),
				quoteNumber: quoteNumber,
				// * These are mandatory fields in the graphql schema so we add a default value to them
				marginPercentage: sanitizedQuoteDetails.marginPercentage ?? 0,
				overheadPercentage:
					sanitizedQuoteDetails.overheadPercentage ?? 0,
				exchangeRate: sanitizedQuoteDetails.exchangeRate ?? 1,
				localOtNumberOfHours:
					sanitizedQuoteDetails.localOtNumberOfHours,
				exeOtNumberOfHours: sanitizedQuoteDetails.exeOtNumberOfHours,
			}
			const cleanQuoteDetails = cleanObject(updatedQuoteDetails)

			// *  Saves the quote first
			const {
				data: { createQuote: newQuote },
			} = (await API.graphql(
				graphqlOperation(mutations.createQuote, {
					input: cleanQuoteDetails,
				})
			)) as { data: CreateQuoteMutationApi }
			// * Saves the quoteInfo next
			// Gets an array of all the costlines
			if (quoteType === 'blankQuote') {
				await API.graphql(
					graphqlOperation(mutations.createBlankQuoteInfoCostlines, {
						input: {
							quoteId: updatedQuoteDetails.id,
							tenantId: updatedQuoteDetails.tenantId,
							allowInternational:
								updatedQuoteDetails.allowInternational,
							exchangeRate: updatedQuoteDetails.exchangeRate,
							marginPercentage:
								updatedQuoteDetails.marginPercentage,
							overheadPercentage:
								updatedQuoteDetails.overheadPercentage,
							localOtNumberOfHours:
								updatedQuoteDetails.localOtNumberOfHours,
							exeOtNumberOfHours:
								updatedQuoteDetails.exeOtNumberOfHours,
						},
						quoteType: quoteType,
					})
				)
			} else {
				await API.graphql(
					graphqlOperation(mutations.createQuoteFromJson, {
						input: {
							quoteId: updatedQuoteDetails.id,
							tenantId: updatedQuoteDetails.tenantId,
							allowInternational:
								updatedQuoteDetails.allowInternational,
							exchangeRate: updatedQuoteDetails.exchangeRate,
							localExchangeRate:
								updatedQuoteDetails.localExchangeRate,
							marginPercentage:
								updatedQuoteDetails.marginPercentage,
							overheadPercentage:
								updatedQuoteDetails.overheadPercentage,
							localOtNumberOfHours:
								updatedQuoteDetails.localOtNumberOfHours,
							exeOtNumberOfHours:
								updatedQuoteDetails.exeOtNumberOfHours,
						},
						quoteType: quoteType,
						budgetSections: budgetSections,
						quoteJson: quoteJsonDetails,
					})
				)
			}

			// We update the parent project to add the projectType
			if (!parsedParentProjectData?.projectType && parentProjectId) {
				await API.graphql(
					graphqlOperation(mutations.updateProjects, {
						input: {
							id: parentProjectId,
							// We keep the parent project type as the blankQuoteType if the quoteType is blankQuote
							projectType:
								quoteType === 'blankQuote'
									? updatedQuoteDetails?.blankQuoteType
									: quoteType,
						},
					})
				)
				// We invalidate the parent project to update the projectType
				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: parentProjectId },
					])
				)
			}

			// We update the company details to account for change in projectCount
			const updatedCompanyInfoDetails: Partial<ReduxCompany> = {
				id: oldCompanyInfoDetails?.id,
				quoteCount: companyCount,
			}
			if (oldCompanyInfoDetails?.useOneQuoteNumber) {
				updatedCompanyInfoDetails.commonQuoteNumber = Number.parseInt(
					zeroPad(
						(oldCompanyInfoDetails.commonQuoteNumber || 0) + 1,
						4
					)
				)
			}

			// Invalidate projectChildren cache to refetch instead of manual merge
			if (parentProjectId) {
				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: `${parentProjectId}-children` },
					])
				)
			}
			makeSuccessToast(tProjectChildren('quote.successMsg.create'))
			// We will not update CompanyInfo if user is freelancer and he is creating a quote within his archive
			if (
				userTableInfo.cognitoGroup?.includes('freelancer') &&
				userTableInfo.tenantId === userTableInfo.currentCompanyTenantId
			) {
				return
			}
			await updateCompanyInfoWithDeltaSyncSuppress(
				updatedCompanyInfoDetails
			)

			return
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const createQuoteFromTemplate = createAsyncThunk<
	void,
	{ quoteId: string; parentProjectId: string; quoteType: string },
	{
		dispatch: AppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/createQuoteFromTemplate',
	async (
		{ quoteId, parentProjectId, quoteType },
		{ dispatch, getState, rejectWithValue }
	) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const tenantId = userTableInfo.currentCompanyTenantId
			dispatch(setFetchProjectApiStatus('loading'))

			const {
				data: { getCompany: oldCompanyInfoDetails },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, { id: tenantId })
			)) as {
				data: GetCompanyQueryApi
			}

			const { companyCount, newTypeCount, newTypeYear } =
				getQuoteOrProjectNumberDetails({
					// @ts-expect-error
					companyCount:
						oldCompanyInfoDetails?.quoteCount &&
						oldCompanyInfoDetails?.quoteCount.length > 0 &&
						oldCompanyInfoDetails?.quoteCount[0] !== null
							? oldCompanyInfoDetails?.quoteCount
							: [],
					type: quoteType,
				})

			const quoteNumber = getQuoteNumberNew({
				newTypeCount,
				newTypeYear,
				type: quoteType,
				useOneQuoteNumber:
					oldCompanyInfoDetails?.useOneQuoteNumber ?? false,
				commonQuoteNumber:
					oldCompanyInfoDetails?.commonQuoteNumber ?? 0,
				// @ts-expect-error
				quoteDetails:
					oldCompanyInfoDetails?.quoteCount &&
					oldCompanyInfoDetails?.quoteCount.length > 0 &&
					oldCompanyInfoDetails?.quoteCount[0] !== null
						? oldCompanyInfoDetails?.quoteCount
						: [],
				quoteCustomisationDetails:
					oldCompanyInfoDetails?.quoteNumberCustomisation &&
					oldCompanyInfoDetails?.quoteNumberCustomisation.length >
						0 &&
					oldCompanyInfoDetails?.quoteNumberCustomisation[0] !== null
						? oldCompanyInfoDetails?.quoteNumberCustomisation
						: [],
			})
			// We run the lambda to create the new quote from template
			await API.graphql(
				graphqlOperation(mutations.newQuoteFromTemplate, {
					quoteId,
					parentProjectId,
					quoteNumber,
				})
			)

			makeSuccessToast(tProjectChildren('quote.successMsg.create'))
			// We will not update CompanyInfo if user is freelancer and he is creating a quote within his archive
			if (
				userTableInfo.cognitoGroup?.includes('freelancer') &&
				userTableInfo.tenantId === userTableInfo.currentCompanyTenantId
			) {
				return
			}
			// We update the company details to account for change in projectCount
			const updatedCompanyInfoDetails: Partial<ReduxCompany> = {
				id: oldCompanyInfoDetails?.id,
				quoteCount: companyCount,
			}
			if (oldCompanyInfoDetails?.useOneQuoteNumber) {
				updatedCompanyInfoDetails.commonQuoteNumber = Number.parseInt(
					zeroPad(
						(oldCompanyInfoDetails.commonQuoteNumber || 0) + 1,
						4
					)
				)
			}
			await updateCompanyInfoWithDeltaSyncSuppress(
				updatedCompanyInfoDetails
			)
			// We invalidate the parent project to update the projectType
			dispatch(
				projectApi.util.invalidateTags([
					{ type: 'Projects', id: parentProjectId },
				])
			)

			dispatch(setFetchProjectApiStatus('succeeded'))

			dispatch(
				projectApi.util.invalidateTags([
					{ type: 'Projects', id: `${parentProjectId}-children` },
				])
			)

			return
		} catch (err: any) {
			makeErrorToast(msg)
			dispatch(setFetchProjectApiStatus('failed'))
			return rejectWithValue(err)
		}
	}
)

export const createQuoteWithCostlinesFilled = createAsyncThunk<
	undefined,
	{
		parentProjectId: string
		quoteName: string
		marginPercentage: number
		overheadPercentage: number
		totalBudget: number
		totalCrewBudget: number
		totalPostProdBudget: number
		crewPrepNumberOfDays: number
		crewShootNumberOfDays: number
	},
	{
		dispatch: AppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/createQuoteWithCostlinesFilled',
	async (
		{
			parentProjectId,
			quoteName,
			marginPercentage,
			overheadPercentage,
			totalBudget,
			totalCrewBudget,
			totalPostProdBudget = 0,
			crewPrepNumberOfDays,
			crewShootNumberOfDays,
		},
		{ dispatch, getState, rejectWithValue }
	) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const quoteDetails = {
				clientId: null,
				contactId: null,
				quoteHeaderValue1: '',
				quoteHeaderValue2: '',
				quoteHeaderValue3: '',
				quoteHeaderValue4: '',
				quoteHeaderValue5: '',
				quoteHeaderValue6: '',
				quoteHeaderValue7: '',
				quoteHeaderValue8: '',
				quoteHeaderValue9: '',
				name: `AI Budget - ${nanoid().slice(0, 4)}`,
				quoteHeaderLabel1: 'Client',
				quoteHeaderLabel2: 'Director',
				quoteHeaderLabel3: 'Project',
				quoteHeaderLabel4: 'Executive Producer',
				quoteHeaderLabel5: 'Contact',
				quoteHeaderLabel6: 'Line Producer',
				quoteHeaderLabel7: 'Agency',
				quoteHeaderLabel8: 'Number of Shooting Days',
				quoteHeaderLabel9: 'Agency Representative',
				notes: '',
				selectContactById: false,
				companyLogo: 'logoImage' as const,
				quoteCurrency: 'EUR',
				quoteLanguage: 'fr',
				marginPercentage: 15,
				overheadPercentage: 10,
				localExchangeRate: 1,
				localOtNumberOfHours: 8,
				exeLocation: 'France',
				billLocation: 'Exe',
				allowInternational: false,
				introduction: '',
				shooting: '',
				prep: '',
				shootingCountry: '',
				prelight: '',
				casting: '',
				crew: '',
				buyouts: '',
				locations: '',
				studio: '',
				artDepartment: '',
				wardrobe: '',
				equipment: '',
				travel: '',
				accommodation: '',
				editing: '',
				imagePostproduction: '',
				soundPostproduction: '',
				deliverables: '',
				insurance: '',
				quoteGuidelines: '',
				excluded: '',
				paymentTerms: '',
				miscellaneous: '',
				conclusion: '',
				budgetSections: [
					'Preproduction',
					'Talent',
					'Crew',
					'Equipment',
					'Art Department',
					'Studio',
					'Location',
					'Unit',
					'Vehicles',
					'Travel & Accommodation',
					'Production Misc.',
					'Postproduction - Image',
					'Postproduction - Sound',
					'Payroll Taxes',
					'Insurance',
				],
			}
			const { payload: quoteId } = await dispatch(
				createQuote({
					parentProjectId,
					quoteDetails: {
						...quoteDetails,
						name: quoteName,
						marginPercentage,
						overheadPercentage,
						filledCostlineDetails: {
							totalBudget,
							totalCrewBudget,
							totalPostProdBudget,
							crewPrepNumberOfDays,
							crewShootNumberOfDays,
						},
					},
					quoteType: 'frenchApfp',
				})
			)
			if (!quoteId) {
				return rejectWithValue('Error creating quote')
			}
			console.log('quoteId: ', quoteId)
			await API.graphql(
				graphqlOperation(mutations.fillCostlinesFromBudgetTotal, {
					quoteId,
					totalBudget,
					totalCrewBudget,
					totalPostProdBudget,
					crewShootNumberOfDays,
					crewPrepNumberOfDays,
				})
			)
			dispatch(
				projectApi.util.invalidateTags([
					{ type: 'Projects', id: `${parentProjectId}-children` },
				])
			)
		} catch (err) {
			console.error('err in createQuoteWithCostlinesFilled: ', err)
			const errMsg = getErrorMessage(err)
			makeErrorToast(errMsg)
			return rejectWithValue(errMsg)
		}
	}
)

export const updateQuote = createAsyncThunk<
	void,
	{
		quoteId: string
		newQuote: Partial<ReduxQuote>
		modifier?:
			| 'updateProjectChildren'
			| 'showToast'
			| 'closeUpdateModals'
			| 'dontSetCurrentQuote'
			| 'keepEditModalOpen'
	},
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/updateQuote',
	async (
		{ quoteId, newQuote, modifier },
		{ dispatch, getState, rejectWithValue }
	) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const { auth, settings } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const companyQuoteTemplateList = settings.companyQuoteTemplateList
			// Gets the og quote
			let {
				data: { getQuote: originalQuote },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteId })
			)) as {
				data: { getQuote: ReduxQuote | null }
			}
			originalQuote = checkIfItemIsAccessible(
				userTableInfo,
				originalQuote
			) as ReduxQuote | null
			if (!originalQuote) {
				return rejectWithValue(`Can't update quote`)
			}
			const quoteUpdateParams = getProperUpdateValues({
				updatedInput: newQuote,
				originalInput: originalQuote,
			})
			const sanitizedQuoteUpdateParams =
				sanitizeQuoteProductionNotes(quoteUpdateParams)
			//* can't change these values
			delete sanitizedQuoteUpdateParams.recapInfo
			delete sanitizedQuoteUpdateParams.quoteInfo
			delete sanitizedQuoteUpdateParams.proposals
			delete sanitizedQuoteUpdateParams.freelancers
			delete sanitizedQuoteUpdateParams.childFreelancers
			delete sanitizedQuoteUpdateParams._deleted
			delete sanitizedQuoteUpdateParams._lastChangedAt
			delete sanitizedQuoteUpdateParams.allowInternational
			delete sanitizedQuoteUpdateParams.updatedOn
			// biome-ignore lint/complexity/noForEach: <>
			Object.entries(sanitizedQuoteUpdateParams).forEach(([key, val]) => {
				if (typeof val === 'string') {
					val = val.replaceAll('constructor ', ' Constructor ')
					val = val.replaceAll(' constructor ', ' Constructor ')
					sanitizedQuoteUpdateParams[key] = val
				}
			})
			const updateParams = getProperUpdateValues({
				updatedInput: sanitizedQuoteUpdateParams,
				originalInput: originalQuote,
			})
			if (!hasBusinessChanges(updateParams)) {
				if (modifier === 'keepEditModalOpen') {
					return
				}
				return rejectWithValue('Nothing to update')
			}
			const {
				data: { updateQuote: parsedUpdatedQuote },
			} = (await API.graphql(
				graphqlOperation(mutations.updateQuote, { input: updateParams })
			)) as {
				data: UpdateQuoteMutationApi
			}
			const updateGlobalMargins = Object.hasOwn(
				sanitizedQuoteUpdateParams,
				'marginPercentage'
			)
			const updateGlobalOverheads = Object.hasOwn(
				sanitizedQuoteUpdateParams,
				'overheadPercentage'
			)
			const updateGlobalExchangeRate =
				Object.hasOwn(sanitizedQuoteUpdateParams, 'exchangeRate') ||
				Object.hasOwn(sanitizedQuoteUpdateParams, 'localExchangeRate')
			const updateOT =
				Object.hasOwn(
					sanitizedQuoteUpdateParams,
					'localOtNumberOfHours'
				) ||
				Object.hasOwn(
					sanitizedQuoteUpdateParams,
					'exeOtNumberOfHours'
				) ||
				Object.hasOwn(
					sanitizedQuoteUpdateParams,
					'overtimeHours1Rate'
				) ||
				Object.hasOwn(
					sanitizedQuoteUpdateParams,
					'overtimeHours2Rate'
				) ||
				Object.hasOwn(
					sanitizedQuoteUpdateParams,
					'overtimeHours3Rate'
				) ||
				Object.hasOwn(sanitizedQuoteUpdateParams, 'overtimeHours4Rate')
			const updateQuoteCurrency =
				sanitizedQuoteUpdateParams.hasOwnProperty('quoteCurrency') ||
				sanitizedQuoteUpdateParams.hasOwnProperty('exeQuoteCurrency')
			if (
				updateGlobalMargins ||
				updateGlobalOverheads ||
				updateGlobalExchangeRate ||
				updateOT ||
				updateQuoteCurrency
			) {
				// 1. updates the actual values in the costlines
				await API.graphql(
					graphqlOperation(mutations.updateQuoteMarginsAndOverhead, {
						quoteId,
						updateGlobalMargins,
						updateGlobalOverheads,
						updateGlobalExchangeRate,
						updateOT,
						updateQuoteCurrency,
					})
				)
			}
			if (
				updateGlobalMargins ||
				updateGlobalOverheads ||
				updateGlobalExchangeRate ||
				updateOT
			) {
				// 2. Recalculates the margins and totals
				await API.graphql(
					graphqlOperation(mutations.calculateMarginsAndTotals, {
						quoteId,
					})
				)
				dispatch(fetchQuoteInfo({ quoteId }))
			}
			// Change template quote name if quote Name changes
			const updateQuoteName = Object.hasOwn(quoteUpdateParams, 'name')
			const quoteTemplateDetails = companyQuoteTemplateList?.find(
				(c) => c?.quoteId === quoteId
			)
			console.log('quoteTemplateDetails: ', quoteTemplateDetails)
			if (updateQuoteName && quoteTemplateDetails) {
				const updatedQuoteTemplate = {
					name: quoteUpdateParams.name,
					id: quoteTemplateDetails.id,
				}
				await API.graphql(
					graphqlOperation(mutations.updateCompanyQuoteTemplate, {
						input: updatedQuoteTemplate,
					})
				)
			}
			// Get a list of all parentProjectIds children after saving the new project
			dispatch(
				projectApi.util.invalidateTags([
					{
						type: 'Projects',
						id: `${originalQuote.parentProjectId}-children`,
					},
				])
			)
			if (modifier !== 'dontSetCurrentQuote') {
				dispatch(setCurrentQuote(parsedUpdatedQuote))
			}
			if (
				modifier === 'updateProjectChildren' ||
				modifier === 'showToast'
			) {
				dispatch(fetchAllQuotes({}))
				// When you star quotes you need to refetch project totals
				await dispatch(
					projectApi.util.invalidateTags([{ type: 'ProjectTotals' }])
				)
				await dispatch(
					quoteApi.util.invalidateTags([{ type: 'QuoteTemplate' }])
				)
			}
			if (modifier === 'showToast') {
				makeSuccessToast(tProjectChildren('quote.successMsg.update'))
			}
		} catch (err: any) {
			console.error('err in updateQuote: ', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const updateQuoteOnBlur = createAsyncThunk<
	ReduxQuote,
	{
		quoteId: string
		newQuote: Partial<ReduxQuote>
	},
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/updateQuoteOnBlur',
	async ({ quoteId, newQuote }, { dispatch, getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			// Gets the og quote
			let {
				data: { getQuote: originalQuote },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteId })
			)) as {
				data: { getQuote: ReduxQuote | null }
			}
			originalQuote = checkIfItemIsAccessible(
				userTableInfo,
				originalQuote
			) as ReduxQuote | null
			if (!originalQuote) {
				return rejectWithValue(`Can't update quote`)
			}
			const updateParams = getProperUpdateValues({
				updatedInput: sanitizeQuoteProductionNotes(newQuote),
				originalInput: originalQuote,
			})
			if (!hasBusinessChanges(updateParams)) {
				return rejectWithValue('Nothing to update')
			}
			const {
				data: { updateQuote: parsedUpdatedQuote },
			} = (await API.graphql(
				graphqlOperation(mutations.updateQuote, { input: updateParams })
			)) as {
				data: { updateQuote: ReduxQuote }
			}
			return parsedUpdatedQuote
		} catch (err) {
			const errMsg = getErrorMessage(err)
			makeErrorToast(msg)
			return rejectWithValue(errMsg)
		}
	}
)

export const toggleQuoteOffline = createAsyncThunk<
	void,
	{ quoteId: string },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/toggleQuoteOffline',
	async ({ quoteId }, { dispatch, getState, rejectWithValue }) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const { auth }: any = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			// Gets the og quote
			let {
				data: { getQuote: originalQuote },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteId })
			)) as {
				data: GetQuoteQueryApi
			}
			originalQuote = checkIfItemIsAccessible(
				userTableInfo,
				originalQuote
			)
			if (!originalQuote) {
				return rejectWithValue(`Can't update quote`)
			}
			const quoteUpdateParams = {} as ReduxQuote
			quoteUpdateParams.id = originalQuote.id

			if (originalQuote.offlineIds?.includes(userTableInfo?.email)) {
				await API.graphql(
					graphqlOperation(mutations.removeOfflineIds, {
						quoteId,
						username: auth.userTableInfo.email,
					})
				)
			} else {
				await API.graphql(
					graphqlOperation(mutations.addOfflineIds, {
						quoteId,
						username: userTableInfo.email,
					})
				)
			}

			// Get a list of all parentProjectIds children after saving the new project
			dispatch(
				projectApi.util.invalidateTags([
					{
						type: 'Projects',
						id: `${originalQuote?.parentProjectId}-children`,
					},
				])
			)

			makeSuccessToast(tProjectChildren('quote.successMsg.update'))
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const updateQuoteInfo = createAsyncThunk<
	ReduxQuoteInfo[],
	ReduxQuoteInfo[],
	{
		state: RootState
		rejectValue: string | { oldQuoteInfo: ReduxQuoteInfo[] }
	}
>('quote/updateQuoteInfo', async (rows, { getState, rejectWithValue }) => {
	try {
		const updatedRows = []
		const { quotes } = getState()
		const previousRows = quotes.present.oldQuoteInfo
		// * We are using an array to store promises of all rows that need to be updated or added
		const updateOrNewCostlinePromises = []
		for (let i = 0; i < rows.length; i++) {
			// * new row is not a field in graphql schema it just to check if a newRow has been added
			const eachRow = rows[i]
			// We are using newRows to update the redux state with the new row values, For lines that were updated we need to replace the index number with the updated or added rows
			// First we check if the redux row for quote Info is the same
			if (!isEqual(eachRow, previousRows[i])) {
				const updatePromise = async () => {
					const {
						data: { getQuoteInfo: originalRow },
					} = (await API.graphql(
						graphqlOperation(queries.getQuoteInfo, {
							id: eachRow.id,
						})
					)) as {
						data: GetQuoteInfoQueryApi
					}
					if (originalRow) {
						const updateInput = getProperUpdateValues({
							updatedInput: eachRow,
							originalInput: originalRow,
						})
						if (!hasBusinessChanges(updateInput)) {
							return
						}
						const {
							data: { updateQuoteInfo: updatedRow },
						} = (await API.graphql(
							graphqlOperation(mutations.updateQuoteInfo, {
								input: updateInput,
							})
						)) as { data: UpdateQuoteInfoMutationApi }

						return updatedRow
					}
				}
				updateOrNewCostlinePromises.push(updatePromise())
			} else {
				updatedRows.push(eachRow)
			}
		}
		// All promise are executed at once
		await Promise.all(updateOrNewCostlinePromises)

		return rows
	} catch (err) {
		const errorMessage = getErrorMessage(err)
		if (errorMessage.includes('Conflict resolver rejects mutation')) {
			console.log('updateQuoteInfo costline retry err', err)
			const oldQuoteInfo = (await fetchQuoteInfoUtil(
				rows?.[0]?.quoteId,
				{}
			)) as ReduxQuoteInfo[]
			// const updateQuoteInfoApiCounter =
			// 	getState().quotes.present.updateQuoteInfoApiCounter
			// if (updateQuoteInfoApiCounter === 1) {
			// 	makeErrorToast(
			// 		'Error in updating quote info costline, please press sync button'
			// 	)
			// }
			return rejectWithValue({ oldQuoteInfo })
		}
		makeErrorToast(msg)
		return rejectWithValue(errorMessage)
	}
})

export const addQuoteInfoCostline = createAsyncThunk<
	void,
	ReduxQuoteInfoNewRow[],
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/addQuoteInfoCostline',
	async (rows, { getState, dispatch, rejectWithValue }) => {
		try {
			const { quotes } = getState()
			const previousRows = quotes.present.oldQuoteInfo
			// * We are using an array to store promises of all rows that need to be updated or added
			const updateOrNewCostlinePromises = []
			const newRows = []
			for (let i = 0; i < rows.length; i++) {
				// * new row is not a field in graphql schema it just to check if a newRow has been added
				const { newRow = false, ...eachRow } = rows[i]

				// We are using newRows to update the redux state with the new row values, For lines that were updated we need to replace the index number with the updated or added rows
				newRows.push(eachRow)
				// First we check if the redux row for quote Info is the same
				if (!isEqual(eachRow, previousRows[i])) {
					//*  We are removing version, last changed, at from the rows when comparing because version field should not be updated by user.
					if (!newRow) {
						const updatePromise = async () => {
							const data = (await API.graphql(
								graphqlOperation(queries.getQuoteInfo, {
									id: eachRow.id,
								})
							)) as { data: GetQuoteInfoQueryApi }
							const originalRow = data.data.getQuoteInfo
							if (originalRow) {
								const updateDetails: Partial<ReduxQuoteInfo> =
									{}
								for (const property in originalRow) {
									const typedProperty =
										property as keyof ReduxQuoteInfo
									if (
										!isEqual(
											originalRow[typedProperty],
											eachRow[typedProperty]
										)
									) {
										updateDetails[typedProperty] = eachRow[
											typedProperty
										] as any
									}
								}
								const {
									createdAt,
									updatedAt,
									_lastChangedAt,
									_deleted,
									...parsedUpdatedDetails
								} = updateDetails
								parsedUpdatedDetails.id = originalRow.id

								await API.graphql(
									graphqlOperation(
										mutations.updateQuoteInfo,
										{
											input: parsedUpdatedDetails,
										}
									)
								)
							}
						}
						updateOrNewCostlinePromises.push(updatePromise())
					} else {
						// Creates a new quote if the there is a newRow field exists in the action arg
						const newRowPromise = async () => {
							await API.graphql(
								graphqlOperation(mutations.createQuoteInfo, {
									input: {
										...eachRow,
										freelancers: ['dummyId'],
										childFreelancers: [
											...(quotes.present.oldQuoteInfo[0]
												.childFreelancers || [
												'dummyId',
											]),
										],
										offlineIds: [
											...(quotes.present.oldQuoteInfo[0]
												.offlineIds || ['dummyId']),
										],
									},
								})
							)
						}
						updateOrNewCostlinePromises.push(newRowPromise())
					}
				}
			}
			// All promise are executed at once
			try {
				await Promise.all(updateOrNewCostlinePromises)
			} catch (err: any) {
				makeErrorToast(msg)
			}
			if (rows[0].quoteId) {
				dispatch(fetchQuoteInfo({ quoteId: rows[0].quoteId }))
			}
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const addQuoteInfoCostline2 = createAsyncThunk<
	ReduxQuoteInfo[],
	{ newCostLine: ReduxQuoteInfo; updatedRows: ReduxQuoteInfo[] },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/addQuoteInfoCostline2',
	async (
		{ newCostLine, updatedRows },
		{ getState, dispatch, rejectWithValue }
	) => {
		try {
			const { quotes } = getState()
			const quoteDetails = quotes.present.currentQuote
			const tQuoteGridPage1 = i18next.getFixedT(null, 'QuoteGridPage1')
			if (quoteDetails?.isFinalized) {
				makeErrorToast(tQuoteGridPage1('addCostlineError'))
				return rejectWithValue(
					'Cannot add costline to a finalized quote'
				)
			}
			const firstRow = quotes.present.oldQuoteInfo[0]
			if (!firstRow) {
				throwQuotesError('No first row', {
					action: 'addQuoteInfoCostline2',
					quoteId: quoteDetails?.id,
				})
			}
			await API.graphql(
				graphqlOperation(mutations.createQuoteInfo, {
					input: {
						...newCostLine,
						freelancers: ['dummyId'],
						childFreelancers: [
							...(firstRow.childFreelancers || ['dummyId']),
						],
						offlineIds: [...(firstRow.offlineIds || ['dummyId'])],
					},
				})
			)
			return updatedRows
		} catch (error) {
			console.log('error adding costline: ', error)
			const errMsg = getErrorMessage(error)
			makeErrorToast(errMsg)
			return rejectWithValue(errMsg)
		}
	}
)

export const deleteQuoteInfo = createAsyncThunk<
	ReduxQuoteInfo[],
	{ oldCostline: ReduxQuoteInfo; updatedRows: ReduxQuoteInfo[] },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/deleteQuoteInfo',
	async (
		{ oldCostline, updatedRows },
		{ dispatch, getState, rejectWithValue }
	) => {
		const tQuoteGridPage1 = i18next.getFixedT(null, 'QuoteGridPage1')
		try {
			const { quotes } = getState()
			const quoteDetails = quotes?.present?.currentQuote
			if (quoteDetails?.isFinalized) {
				makeErrorToast(tQuoteGridPage1('deleteCostlineError'))
				return rejectWithValue(
					'Cannot delete costline of a finalized quote'
				)
			}
			const {
				data: { getQuoteInfo: originalRow },
			} = (await API.graphql(
				graphqlOperation(queries.getQuoteInfo, {
					id: oldCostline.id,
				})
			)) as { data: GetQuoteInfoQueryApi }
			if (!originalRow || !originalRow?.id) {
				throwQuotesError('No original row', {
					action: 'deleteQuoteInfo',
					quoteId: quoteDetails?.id,
					costlineId: oldCostline.id,
				})
				throw new Error('No original row')
			}
			const {
				data: { updateQuoteInfo },
			} = (await API.graphql(
				graphqlOperation(mutations.updateQuoteInfo, {
					input: {
						id: originalRow.id,
						activeFlag: false,
					},
				})
			)) as { data: UpdateQuoteInfoMutationApi }
			await API.graphql(
				graphqlOperation(mutations.deleteQuoteInfo, {
					input: {
						id: originalRow.id,
					},
				})
			)
			const recapInfoArray = await fetchRecapInfoUtil(oldCostline.quoteId)
			const oldRecapCostline = recapInfoArray.find(
				(r) => r.uuid === oldCostline.id
			)
			if (oldRecapCostline) {
				const updatedRow = {
					id: oldRecapCostline.id,
					activeFlag: false,
				}
				const {
					data: { updateRecapInfo },
				} = (await API.graphql(
					graphqlOperation(mutations.updateRecapInfo, {
						input: updatedRow,
					})
				)) as { data: UpdateRecapInfoMutationApi }
				if (updateRecapInfo?.id) {
					await API.graphql(
						graphqlOperation(mutations.deleteRecapInfo, {
							input: {
								id: oldRecapCostline.id,
							},
						})
					)
				}
			}

			await dispatch(
				calculateMarginsAndTotals({
					quoteId: oldCostline.quoteId,
				})
			)
			await delayInMs(200)
			dispatch(fetchQuoteInfo({ quoteId: oldCostline.quoteId }))
			return updatedRows
		} catch (error) {
			console.log('error adding costline: ', error)
			const errMsg = getErrorMessage(error)
			makeErrorToast(errMsg)
			return rejectWithValue(errMsg)
		}
	}
)

export const addRecapQuoteInfoCostline = createAsyncThunk<
	ReduxRecapInfo[],
	ReduxRecapInfoNewRow[],
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/addRecapQuoteInfoCostline',
	async (rows, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth, quotes } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const quoteFilter = getCostlineFetchFilter(userTableInfo)
			const updatedRows = []
			const previousRows: ReduxRecapInfo[] = await fetchRecapInfoUtil(
				quotes?.present.currentQuote?.id,
				quoteFilter
			)

			const updateOrNewCostlinePromises = []
			const newRows = []
			for (let i = 0; i < rows.length; i++) {
				const { newRow = false, ...eachRow } = rows[i]
				newRows.push(eachRow)
				const originalRow = previousRows.find(
					(row) => row.id === eachRow.id
				)
				if (originalRow) {
					if (!isEqual(eachRow, originalRow) && !newRow) {
						const copyUpdatedRow: Partial<typeof eachRow> = {
							...eachRow,
						}
						delete copyUpdatedRow._lastChangedAt
						const updatePromise = async () => {
							const updatedRow: Partial<ReduxRecapInfo> = {}
							for (const property in copyUpdatedRow) {
								const typedProperty =
									property as keyof typeof copyUpdatedRow
								// Only updates the property that is different
								if (
									!isEqual(
										copyUpdatedRow[typedProperty],
										originalRow[typedProperty]
									)
								) {
									updatedRow[typedProperty] = copyUpdatedRow[
										typedProperty
									] as any
								}
							}
							updatedRow.id = originalRow.id
							delete updatedRow._deleted
							delete updatedRow._lastChangedAt
							delete updatedRow.createdAt
							delete updatedRow.updatedAt
							delete updatedRow.uuid

							const {
								data: { updateRecapInfo: apiUpdatedRow },
							} = (await API.graphql(
								graphqlOperation(mutations.updateRecapInfo, {
									input: updatedRow,
								})
							)) as { data: UpdateRecapInfoMutationApi }

							return apiUpdatedRow
						}
						updateOrNewCostlinePromises.push(updatePromise())
						console.count('Updated Rows')
					} else {
						updatedRows.push(eachRow)
						console.count('Non updated Rows')
					}
				} else if (newRow) {
					console.log('newRow: ', newRow)
					const {
						data: { createRecapInfo: apiNewRow },
					} = (await API.graphql(
						graphqlOperation(mutations.createRecapInfo, {
							input: eachRow,
						})
					)) as { data: CreateRecapInfoMutationApi }
					newRows[i] = apiNewRow
				}
			}
			await Promise.all(updateOrNewCostlinePromises)
			await delayInMs(200)
			const newUpdatedRows: ReduxRecapInfo[] = await fetchRecapInfoUtil(
				quotes?.present.currentQuote?.id,
				quoteFilter
			)
			const sortedRecapInfo = orderBy(
				newUpdatedRows,
				['tableRowIndex'],
				['asc']
			)
			for (const r of sortedRecapInfo) {
				console.log('Updated rows Index: ', r.tableRowIndex)
			}
			dispatch(quoteApi.util.invalidateTags([{ type: 'QuoteTotals' }]))
			return sortedRecapInfo
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const addRecapQuoteInfoCostline2 = createAsyncThunk<
	ReduxRecapInfo[],
	{ newCostLine: ReduxRecapInfo; updatedRows: ReduxRecapInfo[] },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/addRecapQuoteInfoCostline2',
	async (
		{ newCostLine, updatedRows },
		{ getState, dispatch, rejectWithValue }
	) => {
		try {
			const { quotes } = getState()
			const firstRow = quotes.present.oldRecapQuoteInfo[0]
			if (!firstRow) {
				throwQuotesError('No first row', {
					action: 'addRecapQuoteInfoCostline2',
					quoteId: quotes.present.currentQuote?.id,
				})
			}
			await API.graphql(
				graphqlOperation(mutations.createRecapInfo, {
					input: {
						...newCostLine,
						freelancers: ['dummyId'],
						childFreelancers: [
							...(firstRow.childFreelancers || ['dummyId']),
						],
						offlineIds: [...(firstRow.offlineIds || ['dummyId'])],
					},
				})
			)
			return updatedRows
		} catch (error) {
			console.log('error adding costline: ', error)
			const errMsg = getErrorMessage(error)
			makeErrorToast(errMsg)
			return rejectWithValue(errMsg)
		}
	}
)

export const updateRecapQuoteInfo = createAsyncThunk<
	ReduxRecapInfo[],
	ReduxRecapInfo[],
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string | { oldRecapQuoteInfo: ReduxRecapInfo[] }
	}
>(
	'quote/updateRecapQuoteInfo',
	async (rows, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth, quotes } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const quoteFilter = getCostlineFetchFilter(userTableInfo)
			const previousRows = quotes.present.oldRecapQuoteInfo
			const updatedRows = []

			const updateOrNewCostlinePromises = []
			for (let i = 0; i < rows.length; i++) {
				const eachRow = rows[i]
				const prevRow = previousRows[i]
				// We are using newRows to update the redux state with the new row values, For lines that were updated we need to replace the index number with the updated or added rows
				// First we check if the redux row for quote Info is the same
				if (!isEqual(eachRow, prevRow)) {
					const updatePromise = async () => {
						const {
							data: { getRecapInfo: originalRow },
						} = (await API.graphql(
							graphqlOperation(queries.getRecapInfo, {
								id: eachRow.id,
							})
						)) as {
							data: { getRecapInfo: ReduxRecapInfo }
						}
						if (!originalRow) {
							return
						}
						const updateInput = getProperUpdateValues({
							updatedInput: eachRow,
							originalInput: originalRow,
						})

						if (!hasBusinessChanges(updateInput)) {
							return
						}
						const {
							data: { updateRecapInfo: updatedRow },
						} = (await API.graphql(
							graphqlOperation(mutations.updateRecapInfo, {
								input: updateInput,
							})
						)) as { data: UpdateRecapInfoMutationApi }

						// Update purchase table if purchaseId exists
						if (!originalRow.purchaseId) {
							return updatedRow
						}

						const basePurchaseVariables = ['purchaseId']
						const updatePurchaseData = pick(updateInput, [
							'provisionalTotal',
						]) as Partial<ReduxPurchase>
						if (updateInput.item)
							updatePurchaseData.recapCostlineItem =
								updateInput.item
						if (updateInput.item_fr)
							updatePurchaseData.recapCostlineItem_fr =
								updateInput.item_fr
						updatePurchaseData.id = originalRow.purchaseId
						if (
							checkNewObjectKeysPresent(
								updatePurchaseData,
								basePurchaseVariables
							)
						) {
							dispatch(
								updatePurchase({
									purchaseData: updatePurchaseData,
								})
							)
						}

						return updatedRow
					}
					updateOrNewCostlinePromises.push(updatePromise())
				} else {
					updatedRows.push(eachRow)
				}
			}
			await Promise.all(updateOrNewCostlinePromises)
			dispatch(quoteApi.util.invalidateTags([{ type: 'QuoteTotals' }]))
			return rows
		} catch (err) {
			const errorMessage = getErrorMessage(err)
			if (errorMessage.includes('Conflict resolver rejects mutation')) {
				console.log('updateQuoteInfo costline retry err', err)
				const quoteId = rows?.[0]?.quoteId
				const oldQuoteInfo = await fetchQuoteInfoUtil(quoteId)
				console.error(
					'oldQuoteInfo error in updateRecapInfo costline: ',
					oldQuoteInfo?.[0]
				)
				return rejectWithValue(
					'Error in updating recap info costline, please press sync button'
				)
			}
			makeErrorToast(msg)
			return rejectWithValue(errorMessage)
		}
	}
)

export const deleteQuote = createAsyncThunk<
	void,
	{ quote: ReduxQuote },
	{
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>('quote/deleteQuote', async ({ quote }, { dispatch, rejectWithValue }) => {
	// Prevent deletion of invoice quotes
	if (quote.type === 'invoiceQuote') {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		makeErrorToast(tProjectChildren('quote.deleteInvoiceQuoteDisabled'))
		return rejectWithValue('Invoice quotes cannot be deleted')
	}

	try {
		await API.graphql(
			graphqlOperation(mutations.deleteQuoteById, { quoteId: quote.id })
		)
		dispatch(
			projectApi.util.invalidateTags([
				{
					type: 'Projects',
					id: `${quote.parentProjectId}-children`,
				},
			])
		)
	} catch (err: any) {
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

export const refreshRecapQuoteInfo = createAsyncThunk<
	void,
	string,
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/refreshRecapQuoteInfo',
	async (quoteId, { dispatch, getState, rejectWithValue }) => {
		const tQuoteGridPage1 = i18next.getFixedT(null, 'QuoteGridPage1')
		try {
			await API.graphql(
				graphqlOperation(mutations.syncRecapInfo, { quoteId })
			)
			dispatch(fetchRecapQuoteInfo({ quoteId }))
			dispatch(quoteApi.util.invalidateTags([{ type: 'QuoteTotals' }]))
		} catch (err) {
			const message = getErrorMessage(err)
			makeErrorToast(tQuoteGridPage1('syncLambdaError'))
			console.error('refreshRecapQuoteInfo message: ', message)
			return rejectWithValue(message)
		}
	}
)

export const addFractionalSortKeyToQuote = createAsyncThunk<
	void,
	string,
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/addFractionalSortKeyToQuote',
	async (quoteId, { dispatch, getState, rejectWithValue }) => {
		const tQuoteGridPage1 = i18next.getFixedT(null, 'QuoteGridPage1')
		try {
			await API.graphql(
				graphqlOperation(mutations.addFractionalSortKey, { quoteId })
			)
			dispatch(fetchQuoteInfo({ quoteId }))
		} catch (err) {
			const message = getErrorMessage(err)
			makeErrorToast(tQuoteGridPage1('syncLambdaError'))
			console.error('addFractionalSortKeyToQuote message: ', message)
			return rejectWithValue(message)
		}
	}
)

export const updateCostLineLabel = createAsyncThunk<
	void,
	string,
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/updateCostLineLabel',
	async (quoteId, { dispatch, getState, rejectWithValue }) => {
		const tQuoteGridPage1 = i18next.getFixedT(null, 'QuoteGridPage1')
		try {
			await API.graphql(
				graphqlOperation(mutations.updateCostLineLabel, { quoteId })
			)
			dispatch(fetchQuoteInfo({ quoteId }))
		} catch (err) {
			const message = getErrorMessage(err)
			makeErrorToast(tQuoteGridPage1('syncLambdaError'))
			console.error('updateCostLineLabel message: ', message)
			return rejectWithValue(message)
		}
	}
)

export const duplicateQuote = createAsyncThunk<
	void,
	{
		quoteId: string
		bidLetter: boolean
		recapTable: boolean
		budgetColumn: string[]
		budgetCategories: string[]
	},
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/duplicateQuote',
	async (
		{ quoteId, bidLetter, recapTable, budgetColumn, budgetCategories },
		{ dispatch, getState, rejectWithValue }
	) => {
		/*
		 * Fetch Quote Details, QuoteInfo & recapQuoteInfo details
		 * Create a new name for the quote, remove id, createdAt, updatedAt & isStarred.
		 * Save Quote Details first, use the id from quote details to update quoteId field of QuoteInfo & recapQuoteInfo
		 * Remove version from QuoteInfo & recapQuoteInfo
		 * Use Promise.all to parallel create all the new quoteInfo & recapQuoteInfo items
		 * Add quote as part of parentProject
		 */
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			let {
				data: { getQuote: parsedOriginalQuoteDetails },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteId })
			)) as {
				data: GetQuoteQueryApi
			}
			parsedOriginalQuoteDetails = checkIfItemIsAccessible(
				userTableInfo,
				parsedOriginalQuoteDetails
			)
			const tenantId = userTableInfo.currentCompanyTenantId
			if (!tenantId) {
				throwQuotesError('Current company tenant id is not set', {
					action: 'duplicateQuote',
					quoteId,
					userId: userTableInfo.id,
				})
				throw new Error('Current company tenant id is not set')
			}
			const {
				data: { getCompany: oldCompanyInfoDetails },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, { id: tenantId })
			)) as {
				data: GetCompanyQueryApi
			}
			const quoteType = parsedOriginalQuoteDetails?.quoteType as string

			const { companyCount, newTypeCount, newTypeYear } =
				getQuoteOrProjectNumberDetails({
					// @ts-expect-error
					companyCount:
						oldCompanyInfoDetails?.quoteCount &&
						oldCompanyInfoDetails?.quoteCount.length > 0 &&
						oldCompanyInfoDetails?.quoteCount[0] !== null
							? oldCompanyInfoDetails?.quoteCount
							: [],
					type: quoteType,
				})
			const quoteNumber = getQuoteNumberNew({
				newTypeCount,
				newTypeYear,
				type: quoteType,
				useOneQuoteNumber:
					oldCompanyInfoDetails?.useOneQuoteNumber ?? false,
				commonQuoteNumber:
					oldCompanyInfoDetails?.commonQuoteNumber ?? 0,
				// @ts-expect-error
				quoteDetails:
					oldCompanyInfoDetails?.quoteCount &&
					oldCompanyInfoDetails?.quoteCount.length > 0 &&
					oldCompanyInfoDetails?.quoteCount[0] !== null
						? oldCompanyInfoDetails?.quoteCount
						: [],
				quoteCustomisationDetails:
					oldCompanyInfoDetails?.quoteNumberCustomisation &&
					oldCompanyInfoDetails?.quoteNumberCustomisation.length >
						0 &&
					oldCompanyInfoDetails?.quoteNumberCustomisation[0] !== null
						? oldCompanyInfoDetails?.quoteNumberCustomisation
						: [],
			})

			console.log('quoteNumber: ', quoteNumber)
			console.log('newTypeYear: ', newTypeYear)
			console.log('newTypeCount: ', newTypeCount)
			await API.graphql(
				graphqlOperation(mutations.duplicateQuote, {
					quoteId: quoteId,
					quoteNumber: quoteNumber,
					bidLetter,
					recapTable,
					budgetColumn,
					budgetCategories,
				})
			)
			if (parsedOriginalQuoteDetails?.parentProjectId) {
				// Invalidate project children cache to refresh the list
				dispatch(
					projectApi.util.invalidateTags([
						{
							type: 'Projects',
							id: `${parsedOriginalQuoteDetails?.parentProjectId}-children`,
						},
					])
				)
			}
			makeSuccessToast(tProjectChildren('quote.successMsg.duplicate'))

			const updatedCompanyInfoDetails: Partial<ReduxCompany> = {
				id: tenantId,
				duplicateQuoteCounter:
					oldCompanyInfoDetails?.duplicateQuoteCounter
						? oldCompanyInfoDetails?.duplicateQuoteCounter + 1
						: 1,
				quoteCount: companyCount,
			}
			if (oldCompanyInfoDetails?.useOneQuoteNumber) {
				updatedCompanyInfoDetails.commonQuoteNumber = Number.parseInt(
					zeroPad(
						(oldCompanyInfoDetails.commonQuoteNumber || 0) + 1,
						4
					)
				)
			}
			await updateCompanyInfoWithDeltaSyncSuppress(
				updatedCompanyInfoDetails
			)
		} catch (err: any) {
			const errMsg = getErrorMessage(err)
			makeErrorToast(msg)
			console.error('duplicateQuote error message: ', errMsg)
			return rejectWithValue(errMsg)
		}
	}
)

export const moveQuote = createAsyncThunk<
	void,
	{
		quoteId: string
		parentProjectId: string
		currentParentProjectId: string
		destinationTenantId?: string
	},
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/moveQuote',
	async (
		{
			quoteId,
			parentProjectId,
			currentParentProjectId,
			destinationTenantId,
		},
		{ dispatch, getState, rejectWithValue }
	) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const {
				data: { getQuote: parsedOriginalQuoteDetails },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteId })
			)) as {
				data: GetQuoteQueryApi
			}
			const tenantId = destinationTenantId
			const {
				data: { getCompany: oldCompanyInfoDetails },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, { id: tenantId })
			)) as {
				data: GetCompanyQueryApi
			}
			const {
				data: { getProjects: parsedParentProjectData },
			} = (await API.graphql(
				graphqlOperation(queries.getProjects, {
					id: parentProjectId,
				})
			)) as {
				data: GetProjectsQueryApi
			}
			const quoteType = parsedOriginalQuoteDetails?.quoteType as string

			const { companyCount, newTypeCount, newTypeYear } =
				getQuoteOrProjectNumberDetails({
					// @ts-expect-error
					companyCount:
						oldCompanyInfoDetails?.quoteCount &&
						oldCompanyInfoDetails?.quoteCount.length > 0 &&
						oldCompanyInfoDetails?.quoteCount[0] !== null
							? oldCompanyInfoDetails?.quoteCount
							: [],
					type: quoteType,
				})
			const quoteNumber = getQuoteNumberNew({
				newTypeCount,
				newTypeYear,
				type: quoteType,
				useOneQuoteNumber:
					oldCompanyInfoDetails?.useOneQuoteNumber ?? false,
				commonQuoteNumber:
					oldCompanyInfoDetails?.commonQuoteNumber ?? 0,
				// @ts-expect-error
				quoteDetails:
					oldCompanyInfoDetails?.quoteCount &&
					oldCompanyInfoDetails?.quoteCount.length > 0 &&
					oldCompanyInfoDetails?.quoteCount[0] !== null
						? oldCompanyInfoDetails?.quoteCount
						: [],
				quoteCustomisationDetails:
					oldCompanyInfoDetails?.quoteNumberCustomisation &&
					oldCompanyInfoDetails?.quoteNumberCustomisation.length >
						0 &&
					oldCompanyInfoDetails?.quoteNumberCustomisation[0] !== null
						? oldCompanyInfoDetails?.quoteNumberCustomisation
						: [],
			})
			await API.graphql(
				graphqlOperation(mutations.moveQuote, {
					quoteId: quoteId,
					parentProjectId: parentProjectId,
					quoteNumber: quoteNumber,
				})
			)
			const updatedCompanyInfoDetails: Partial<ReduxCompany> = {
				id: tenantId,
				duplicateQuoteCounter:
					oldCompanyInfoDetails?.duplicateQuoteCounter
						? oldCompanyInfoDetails?.duplicateQuoteCounter + 1
						: 1,
				quoteCount: companyCount,
			}
			if (oldCompanyInfoDetails?.useOneQuoteNumber) {
				updatedCompanyInfoDetails.commonQuoteNumber = Number.parseInt(
					zeroPad(
						(oldCompanyInfoDetails.commonQuoteNumber || 0) + 1,
						4
					)
				)
			}
			if (
				userTableInfo?.cognitoGroup?.includes('freelancer') &&
				oldCompanyInfoDetails?.id
			) {
				await updateCompanyInfoWithDeltaSyncSuppress(
					updatedCompanyInfoDetails
				)
			}

			// We update the parent project to add the projectType
			if (
				!parsedParentProjectData?.projectType &&
				parentProjectId &&
				parsedParentProjectData?._version
			) {
				const input = {
					id: parentProjectId,
					// We keep the parent project type as the blankQuoteType if the quoteType is blankQuote
					projectType:
						quoteType === 'blankQuote'
							? parsedOriginalQuoteDetails?.blankQuoteType
							: quoteType,
				}
				console.log('input: ', input)
				await API.graphql(
					graphqlOperation(mutations.updateProjects, {
						input,
					})
				)
				// We invalidate the parent project to update the projectType
				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: parentProjectId },
					])
				)
			}
			dispatch(
				projectApi.util.invalidateTags([
					{
						type: 'Projects',
						id: `${currentParentProjectId}-children`,
					},
				])
			)

			makeSuccessToast(tProjectChildren('quote.successMsg.move'))
			dispatch(setCurrentQuote(null))
			dispatch(setQuoteMoveDialogOpenStatus(false))
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
		// Closes modal when successful
	}
)

export const removeQuoteSmg = createAsyncThunk<
	void,
	{ quoteId: string },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/removeQuoteSmg',
	async ({ quoteId }, { dispatch, getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const quoteFilter = getCostlineFetchFilter(userTableInfo)
			const parsedQuoteInfo: ReduxQuoteInfo[] = await fetchQuoteInfoUtil(
				quoteId,
				quoteFilter
			)
			const updatedRows = [] as ReduxQuoteInfo[]
			const updateAllCostlinepromises = []
			parsedQuoteInfo.map((costline) => {
				if (costline.smg === null) {
					return costline
				}
				// Sets rate to null in costlines that has smg field and it's qt and nb fields are empty
				if (
					costline.rate === costline.smg &&
					!costline.number &&
					!costline.quantity
				) {
					costline.rate = null
					// We are keeping track of updated costlines in an array
					updatedRows.push(costline)
				}
				return costline
			})
			for (let i = 0; i < updatedRows.length; i++) {
				const updatePromise = async () => {
					const data = (await API.graphql(
						graphqlOperation(queries.getQuoteInfo, {
							id: updatedRows[i].id,
						})
					)) as { data: GetQuoteInfoQueryApi }
					const originalRow = data.data.getQuoteInfo
					if (originalRow) {
						const updateDetails = {
							id: originalRow.id,
							rate: null,
						}
						const updatedRow = await API.graphql(
							graphqlOperation(mutations.updateQuoteInfo, {
								input: updateDetails,
							})
						)
						return updatedRow
					}
				}
				updateAllCostlinepromises.push(updatePromise())
			}
			const updatedValue = await Promise.allSettled(
				updateAllCostlinepromises
			)
			dispatch(fetchQuoteInfo({ quoteId }))
			return
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const addQuoteSmg = createAsyncThunk<
	void,
	{ quoteId: string },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/addQuoteSmg',
	async ({ quoteId }, { dispatch, getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const quoteFilter = getCostlineFetchFilter(userTableInfo)
			const parsedQuoteInfo: ReduxQuoteInfo[] = await fetchQuoteInfoUtil(
				quoteId,
				quoteFilter
			)

			const updatedRows = [] as ReduxQuoteInfo[]
			const updateAllCostlinepromises = []
			parsedQuoteInfo.map((costline) => {
				if (costline.smg === null) {
					return costline
				}
				// Sets costline rate to smg if  rate doesn't exist or is 0
				if (!costline.rate && costline.smg) {
					costline.rate = costline.smg
					// We are keeping track of updated costlines in an array
					updatedRows.push(costline)
				}
				return costline
			})
			for (let i = 0; i < updatedRows.length; i++) {
				const updatePromise = async () => {
					const data = (await API.graphql(
						graphqlOperation(queries.getQuoteInfo, {
							id: updatedRows[i].id,
						})
					)) as { data: GetQuoteInfoQueryApi }
					const originalRow = data.data.getQuoteInfo
					if (originalRow) {
						const updateDetails = {
							id: originalRow.id,
							rate: originalRow.smg,
						}
						const updatedRow = (await API.graphql(
							graphqlOperation(mutations.updateQuoteInfo, {
								input: updateDetails,
							})
						)) as { data: UpdateQuoteInfoMutationApi }
						return updatedRow
					}
				}
				updateAllCostlinepromises.push(updatePromise())
			}
			await Promise.allSettled(updateAllCostlinepromises)
			dispatch(fetchQuoteInfo({ quoteId }))
			return
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const uploadQuote = createAsyncThunk<
	void,
	{ quote: any; projectId: string },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'quote/uploadQuote',
	async ({ quote, projectId }, { getState, dispatch, rejectWithValue }) => {
		try {
			const parsedQuote: any = JSON.parse(quote)
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const tenantId = userTableInfo.currentCompanyTenantId
			const {
				data: { getProjects: parsedParentProjectData },
			} = (await API.graphql(
				graphqlOperation(queries.getProjects, { id: projectId })
			)) as {
				data: GetProjectsQueryApi
			}
			const quoteId = nanoid()
			// * Get quote details working
			// ! field contact doesn't exist in schema when it's fixed remove it from the omit below
			let quoteDetails = omit(parsedQuote, [
				'quoteInfo',
				'recapInfo',
				'parentProjectId',
				'parentCompanyId',
				'createdAt',
				'updatedAt',
				'contact',
			])
			// Add id equal to the new quoteId and omits the old _id
			quoteDetails.id = quoteId
			quoteDetails = omit(quoteDetails, ['_id'])
			quoteDetails.parentProjectId = projectId
			quoteDetails.tenantId = tenantId
			quoteDetails.type = 'quote'
			quoteDetails.activeFlag = true
			// * Assumption is all uploaded quotes have international
			quoteDetails.allowInternational = true
			await API.graphql(
				graphqlOperation(mutations.createQuote, {
					input: {
						...quoteDetails,
						freelancers: ['dummyId'],
						childFreelancers: [
							...(parsedParentProjectData?.childFreelancers || [
								'dummyId',
							]),
						],
						offlineIds: ['dummyId'],
					},
				})
			)

			// * Get quoteInfo working
			const quoteInfo: ReduxQuoteInfo[] = parsedQuote.quoteInfo
			// Adding empty costlines before the total of each subcategory and
			// adding agentCommission costline if it doesn't exist for that subcategory
			// Recalculate tableRowIndex and costlineIds and labels after that
			const quoteInfoWithAgentCommission = quoteInfo.reduce(
				(finalArray: any[], item, index, arr) => {
					const prevItem = arr[index - 1]
					const agentCommissionCostline = []
					let categoryName = ''

					if (prevItem) {
						categoryName = prevItem.category as string
						categoryName = categoryName.replace(' - EXE', '')
					}
					if (item.costLineType === 'subCategoryTotal') {
						// Doesn't add a new empty costlines in these categories
						if (
							!['Insurance', 'Insurance - EXE'].includes(
								item.category as string
							)
						) {
							// Finds if agent Commission Costline exists for the subcategory if it doesn't we add it to empty costlines
							const agentCommissionRowIndex = quoteInfo.findIndex(
								(r) =>
									r.category === prevItem.category &&
									r.subCategory === prevItem.subCategory &&
									r.costLineType === 'costLine' &&
									r.item === 'Agent commission'
							)
							if (agentCommissionRowIndex === -1) {
								// Agent Commission line
								const otherAttributes = {
									activeFlag: true,
									item: 'Agent commission',
									item_fr: 'Commission agent',
									unit: 'Fee',
									marginPercentage: 0,
									overheadPercentage: 0,
									totalMargin: 0,
									allowMarginAndOverheadPercentageGlobalUpdate: false,
								}
								agentCommissionCostline.push({
									...createCostLine(
										categoryName,
										prevItem.subCategory as string,
										prevItem.isInternational as boolean
									),
									...otherAttributes,
								})
							}
						}
					}
					if (agentCommissionCostline.length > 0) {
						// biome-ignore lint/performance/noAccumulatingSpread: <>
						finalArray = [...finalArray, ...agentCommissionCostline]
					}
					finalArray.push(item)
					return finalArray
				},
				[]
			)

			const updatedCostlinesWithLabels: any = createCostLineIdsAndLabels(
				quoteInfoWithAgentCommission
			)
			const idList = {} as any
			updatedCostlinesWithLabels.map((row: any) => {
				// The uploaded uuid is now the id
				const newId = nanoid()
				idList[row.uuid] = newId
				row.id = newId
				row.quoteId = quoteId
				row.tenantId = tenantId
				row.activeFlag = true
				delete row.uuid
				// Parse the attributes to covert text to number
				if (!row.quantity) row.quantity = checkNumber(row.quantity)
				if (!row.number) row.number = checkNumber(row.number)
				if (!row.rate) row.rate = checkNumber(row.rate)
				if (!row.total) row.total = checkNumber(row.total)
				if (!row.agentCommissionPercentage)
					row.agentCommissionPercentage = checkNumber(
						row.agentCommissionPercentage
					)
				if (!row.agentCommission)
					row.agentCommission = checkNumber(row.agentCommission)
				if (!row.marginPercentage)
					row.marginPercentage = checkNumber(row.marginPercentage)
				if (!row.overheadPercentage)
					row.overheadPercentage = checkNumber(row.overheadPercentage)
				if (!row.overtimeHoursCost)
					row.overtimeHoursCost = checkNumber(row.overtimeHoursCost)
				if (!row.overtimeHours)
					row.overtimeHours = checkNumber(row.overtimeHours)
				row.overtimeDetails.map((ot: any) => {
					if (!ot.overtimeHours) {
						ot.overtimeHours = checkNumber(ot.overtimeHours)
					}
					return ot
				})
				return row
			})

			try {
				// Saves all the costline in the db one by one
				await Promise.all(
					updatedCostlinesWithLabels.map(async (item: any) => {
						await API.graphql(
							graphqlOperation(mutations.createQuoteInfo, {
								input: {
									...item,
									freelancers: ['dummyId'],
									childFreelancers: [
										...(parsedParentProjectData?.childFreelancers || [
											'dummyId',
										]),
									],
									offlineIds: ['dummyId'],
								},
							})
						)
					})
				)
			} catch (err: any) {
				// If any of the above costline fails we will get the Quote and delete it along with all related QuoteInfo costlines that were created
				let {
					data: { getQuote: deleteQuote },
				} = (await API.graphql(
					graphqlOperation(queries.getQuote, { id: quoteDetails.id })
				)) as {
					data: GetQuoteQueryApi
				}
				deleteQuote = checkIfItemIsAccessible(
					userTableInfo,
					deleteQuote
				)
				if (deleteQuote) {
					await API.graphql(
						graphqlOperation(mutations.deleteQuoteById, {
							quoteId: deleteQuote.id,
						})
					)
				}

				makeErrorToast(msg)
			}
			const recapIdList: any = {}
			// * Get Recap Info Working
			const recapInfo: any = parsedQuote.recapInfo
			recapInfo.map((row: any) => {
				// tableRowIndex needs to be done first
				row.tableRowIndex = Number.parseInt(row.id as string)
				const newId = nanoid()
				row.id = newId
				recapIdList[row.uuid as string] = newId
				// We update the uuid to the new random Id we created in quoteInfo above
				row.uuid = idList[row.uuid as string]
				row.quoteId = quoteId
				row.tenantId = tenantId
				row.activeFlag = true
				delete row.invoiceDocumentId
				// parse the numbers for empty strings and NaN
				if (!row.provisionalTotal)
					row.provisionalTotal = checkNumber(row.provisionalTotal)
				if (!row.actualTotal)
					row.actualTotal = checkNumber(row.actualTotal)
				return row
			})
			recapInfo.map((row: any) => {
				if (row.isCustomCostLine) {
					row.referenceCostLineUuid =
						recapIdList[row.referenceCostLineUuid]
				}
				return row
			})
			try {
				// Saves all the costline in the db one by one
				await Promise.all(
					recapInfo.map(async (item: any) => {
						await API.graphql(
							graphqlOperation(mutations.createRecapInfo, {
								input: {
									...item,
									freelancers: ['dummyId'],
									childFreelancers: [
										...(parsedParentProjectData?.childFreelancers || [
											'dummyId',
										]),
									],
									offlineIds: ['dummyId'],
								},
							})
						)
					})
				)
			} catch (err: any) {
				// If any of the above costline fails we will get the Quote and delete it along with all related QuoteInfo costlines that were created
				let {
					data: { getQuote: deleteQuote },
				} = (await API.graphql(
					graphqlOperation(queries.getQuote, { id: quoteDetails.id })
				)) as {
					data: GetQuoteQueryApi
				}
				deleteQuote = checkIfItemIsAccessible(
					userTableInfo,
					deleteQuote
				)

				if (deleteQuote) {
					await API.graphql(
						graphqlOperation(mutations.deleteQuoteById, {
							quoteId: deleteQuote.id,
						})
					)
				}
				makeErrorToast(msg)
			}
			// * Refresh project children via RTK Query cache invalidation
			dispatch(
				projectApi.util.invalidateTags([
					{ type: 'Projects', id: `${projectId}-children` },
				])
			)
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const removeSupplierFromPrep = createAsyncThunk<
	void,
	{ recapQuoteInfo: Partial<ReduxRecapInfo> },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/removeSupplierFromPrep',
	async ({ recapQuoteInfo }, { getState, dispatch, rejectWithValue }) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		console.log('recapQuoteInfo: ', recapQuoteInfo)
		try {
			const {
				data: { getRecapInfo },
			} = (await API.graphql(
				graphqlOperation(queries.getRecapInfo, {
					id: recapQuoteInfo?.id,
				})
			)) as { data: { getRecapInfo: ReduxRecapInfo } }
			let updatePurchase = {}
			if (getRecapInfo?.purchaseId) {
				const {
					data: { getPurchase: currentPurchase },
				} = (await API.graphql(
					graphqlOperation(queries.getPurchase, {
						id: getRecapInfo.purchaseId,
					})
				)) as { data: { getPurchase: ReduxPurchase } }

				updatePurchase = {
					id: currentPurchase.id,
					activeFlag: false,
				}
			}
			const updateRecapCostline = {
				id: getRecapInfo.id,
				purchaseTotal: null,
				supplierStatus: null,
				supplierId: null,
				paymentStatus: null,
				purchaseId: null,
			}
			if (Object.keys(updatePurchase).length !== 0) {
				await API.graphql(
					graphqlOperation(mutations.updatePurchase, {
						input: updatePurchase,
					})
				)
			}
			await API.graphql(
				graphqlOperation(mutations.updateRecapInfo, {
					input: updateRecapCostline,
				})
			)
			dispatch(
				fetchRecapQuoteInfo({
					quoteId: getRecapInfo?.quoteId as string,
					calculateRecapInfo: true,
				})
			)

			makeSuccessToast(
				tProjectChildren('quote.successMsg.removeSupplier')
			)
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const removeDateRangeFromRecap = createAsyncThunk<
	void,
	{ recapQuoteInfo: Partial<ReduxRecapInfo> },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/removeDateRangeFromRecap',
	async ({ recapQuoteInfo }, { getState, dispatch, rejectWithValue }) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const {
				data: { getRecapInfo },
			} = (await API.graphql(
				graphqlOperation(queries.getRecapInfo, {
					id: recapQuoteInfo?.id,
				})
			)) as { data: { getRecapInfo: ReduxRecapInfo } }

			const updateRecapCostline = {
				id: getRecapInfo.id,
				startDate: null,
				endDate: null,
				days: null,
			}
			await API.graphql(
				graphqlOperation(mutations.updateRecapInfo, {
					input: updateRecapCostline,
				})
			)
			dispatch(
				fetchRecapQuoteInfo({
					quoteId: getRecapInfo?.quoteId as string,
					calculateRecapInfo: true,
				})
			)

			makeSuccessToast(
				tProjectChildren('quote.successMsg.removeDateRange')
			)
		} catch (err) {
			const errMsg = getErrorMessage(err)
			makeErrorToast(errMsg)
			return rejectWithValue(errMsg)
		}
	}
)
export const assignUserToQuote = createAsyncThunk<
	void,
	{ userId: string; quoteId: string; isQuoteList?: boolean },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/assignUserToQuote',
	async (
		{ userId, quoteId, isQuoteList = false },
		{ getState, dispatch, rejectWithValue }
	) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		console.log('quoteId: ', quoteId)
		console.log('userData: ', userId)
		try {
			const {
				data: { getUserInfo },
			} = (await API.graphql(
				graphqlOperation(queries.getUserInfo, { id: userId })
			)) as {
				data: { getUserInfo: ReduxUserInfo }
			}
			const {
				data: { getQuote },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteId })
			)) as {
				data: { getQuote: ReduxQuote }
			}

			const updateQuote = {
				id: getQuote.id,
				assignedTenantId: getUserInfo?.id,
				assignedTenantName: getUserInfo?.email,
			}
			console.log('updateQuote: ', updateQuote)

			await API.graphql(
				graphqlOperation(mutations.updateQuote, { input: updateQuote })
			)

			dispatch(
				projectApi.util.invalidateTags([
					{
						type: 'Projects',
						id: `${getQuote.parentProjectId}-children`,
					},
				])
			)
			if (isQuoteList) {
				dispatch(fetchAllQuotes({}))
			}
			makeSuccessToast(
				tProjectChildren('quote.successMsg.addAssignedUser')
			)
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const removeAssignUserToQuote = createAsyncThunk<
	void,
	{ quoteId: string; isQuoteList?: boolean },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/removeAssignUserToQuote',
	async (
		{ quoteId, isQuoteList = false },
		{ getState, dispatch, rejectWithValue }
	) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		console.log('quoteId: ', quoteId)
		try {
			const {
				data: { getQuote },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteId })
			)) as {
				data: { getQuote: ReduxQuote }
			}

			const updateQuote = {
				id: getQuote.id,
				assignedTenantId: null,
				assignedTenantName: null,
			}
			console.log('updateQuote: ', updateQuote)

			await API.graphql(
				graphqlOperation(mutations.updateQuote, { input: updateQuote })
			)

			dispatch(
				projectApi.util.invalidateTags([
					{
						type: 'Projects',
						id: `${getQuote.parentProjectId}-children`,
					},
				])
			)
			if (isQuoteList) {
				dispatch(fetchAllQuotes({}))
			}
			makeSuccessToast(
				tProjectChildren('quote.successMsg.removeAssignedUser')
			)
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const mergeQuote = createAsyncThunk<
	void,
	{ primaryQuote: string; secondaryQuotes: string[]; projectId: string },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'quote/mergeQuote',
	async (
		{ primaryQuote, secondaryQuotes, projectId },
		{ getState, dispatch, rejectWithValue }
	) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const {
				data: {
					createMasterQuote: { status, message },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.createMasterQuote, {
					primaryQuote,
					secondaryQuotes,
				})
			)) as {
				data: {
					createMasterQuote: {
						status: string
						message?: string
					}
				}
			}
			if (status === 'error' && message === 'sectionError') {
				makeErrorToast(tProjectChildren('quote:toasts.sectionError'))
				return rejectWithValue(message)
			}
			await delayInMs(500)
			dispatch(
				projectApi.util.invalidateTags([
					{ type: 'Projects', id: `${projectId}-children` },
				])
			)

			makeSuccessToast(tProjectChildren('quote.successMsg.create'))
		} catch (err) {
			const errorMessage = getErrorMessage(err)
			console.error('err mergeQuote: ', errorMessage)
			makeErrorToast(msg)
			return rejectWithValue(errorMessage)
		}
	}
)

export const fetchMergedQuoteList = createAsyncThunk<
	ReduxQuote[],
	{ quoteIds: string[] },
	{ dispatch: ThunkAppDispatch; state: RootState; rejectValue: string }
>(
	'quote/fetchMergedQuoteList',
	async ({ quoteIds }, { getState, dispatch, rejectWithValue }) => {
		try {
			const fetchPromiseArray: Array<
				Promise<{
					data: { getQuote: ReduxQuote }
				}>
			> = []
			quoteIds.map((quoteId) => {
				fetchPromiseArray.push(
					API.graphql(
						graphqlOperation(queries.getQuote, { id: quoteId })
					) as Promise<{
						data: { getQuote: ReduxQuote }
					}>
				)
				return quoteId
			})
			const result = await Promise.all(fetchPromiseArray)
			const quoteList = result.reduce(
				(listMergedQuotesData, apiQuote) => {
					const quote = apiQuote.data.getQuote
					if (quote) {
						listMergedQuotesData.push(quote)
					}
					return listMergedQuotesData
				},
				[] as ReduxQuote[]
			)
			return quoteList
		} catch (err) {
			const errMsg = getErrorMessage(err)
			console.error('err fetchMergedQuoteList: ', errMsg)
			makeErrorToast(msg)
			return rejectWithValue(errMsg)
		}
	}
)

export const toggleSetQuoteExpiry = createAsyncThunk<
	void,
	{ quoteId: string },
	{ dispatch: ThunkAppDispatch; state: RootState; rejectValue: string }
>(
	'quote/toggleSetQuoteExpiry',
	async ({ quoteId }, { getState, dispatch, rejectWithValue }) => {
		try {
			const {
				data: { getQuote },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteId })
			)) as {
				data: { getQuote: ReduxQuote }
			}
			if (getQuote) {
				const tProposals = i18next.getFixedT(null, 'proposals')
				const updateQuoteObject = {
					id: getQuote?.id,
					isExpired: !getQuote?.isExpired,
				}
				console.log('updateQuoteObject: ', updateQuoteObject)
				await API.graphql(
					graphqlOperation(mutations.updateQuote, {
						input: updateQuoteObject,
					})
				)
				dispatch(
					projectApi.util.invalidateTags([
						{
							type: 'Projects',
							id: `${getQuote.parentProjectId}-children`,
						},
					])
				)
				dispatch(fetchAllQuotes({}))
				makeSuccessToast(tProposals('toasts.success'))
			}
		} catch (err) {
			const errMsg = getErrorMessage(err)
			console.error('err fetchMergedQuoteList: ', errMsg)
			makeErrorToast(msg)
			return rejectWithValue(errMsg)
		}
	}
)

export const addSecondaryToMasterQuote = createAsyncThunk<
	void,
	{ primaryQuote: string; secondaryQuotes: string[] },
	{ dispatch: ThunkAppDispatch; state: RootState; rejectValue: string }
>(
	'quote/addSecondaryToMasterQuote',
	async (
		{ primaryQuote, secondaryQuotes },
		{ dispatch, rejectWithValue }
	) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const {
				data: {
					addSecondaryToMasterQuote: { status, message },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.addSecondaryToMasterQuote, {
					primaryQuote,
					secondaryQuotes,
				})
			)) as {
				data: {
					addSecondaryToMasterQuote: {
						status: string
						message?: string
					}
				}
			}
			// We are sending status as success from the backend lambda function
			if (status !== 'success') {
				if (status === 'error' && message === 'sectionError') {
					makeErrorToast(
						tProjectChildren('quote:toasts.sectionError')
					)
					return rejectWithValue(message)
				}
				throwQuotesError(
					'Error in adding secondary quotes to master quote',
					{
						action: 'addSecondaryToMasterQuote',
						primaryQuote,
						secondaryQuoteCount: secondaryQuotes.length,
						status,
						lambdaMessage: message,
					}
				)
			}
			await delayInMs(500)
			dispatch(fetchQuote({ quoteId: primaryQuote }))
			await dispatch(fetchQuoteInfo({ quoteId: primaryQuote }))
			if (status === 'success') {
				makeSuccessToast(
					tProjectChildren('quote.successMsg.addSecondary')
				)
			} else {
				makeErrorToast(msg)
			}
		} catch (error) {
			const errorMessage = getErrorMessage(error)
			makeErrorToast(msg)
			Sentry.captureException(
				`error addSecondaryToMasterQuote:  ${error}`
			)
			return rejectWithValue(errorMessage)
		}
	}
)

export const detachMemberQuoteFromMasterQuote = createAsyncThunk<
	void,
	{ masterQuoteId: string; memberQuoteId: string },
	{ dispatch: ThunkAppDispatch; state: RootState; rejectValue: string }
>(
	'quote/detachMemberQuoteFromMasterQuote',
	async ({ masterQuoteId, memberQuoteId }, { dispatch, rejectWithValue }) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const {
				data: { detachMemberQuoteFromMasterQuote: response },
			} = (await API.graphql(
				graphqlOperation(mutations.detachMemberQuoteFromMasterQuote, {
					masterQuoteId,
					memberQuoteId,
				})
			)) as {
				data: {
					detachMemberQuoteFromMasterQuote: {
						status: string
						reason?: string | null
						blockingQuoteId?: string | null
						message?: string | null
					} | null
				}
			}

			if (!response) {
				throw new Error('Empty detach member quote response')
			}

			if (response.status === 'blocked') {
				const reason = response.reason ?? 'generic'
				makeErrorToast(
					tProjectChildren(
						`quote.detachMember.blocked.${reason}`,
						tProjectChildren('quote.detachMember.blocked.generic')
					)
				)
				return rejectWithValue(reason)
			}

			if (response.status !== 'success') {
				throwQuotesError('Error detaching member quote', {
					action: 'detachMemberQuoteFromMasterQuote',
					masterQuoteId,
					memberQuoteId,
					status: response.status,
					reason: response.reason,
					lambdaMessage: response.message,
				})
			}

			await delayInMs(500)
			const refreshedQuote = await dispatch(
				fetchQuote({ quoteId: masterQuoteId })
			).unwrap()
			await dispatch(fetchQuoteInfo({ quoteId: masterQuoteId }))
			await dispatch(fetchRecapQuoteInfo({ quoteId: masterQuoteId }))
			if (refreshedQuote.mergedQuotes) {
				dispatch(
					fetchMergedQuoteList({
						quoteIds: refreshedQuote.mergedQuotes,
					})
				)
			}
			makeSuccessToast(tProjectChildren('quote.detachMember.success'))
		} catch (error) {
			const errorMessage = getErrorMessage(error)
			makeErrorToast(tProjectChildren('quote.detachMember.error'))
			Sentry.captureException(
				`error detachMemberQuoteFromMasterQuote: ${error}`
			)
			return rejectWithValue(errorMessage)
		}
	}
)

export const syncRecapInfoProv = createAsyncThunk<
	void,
	{ modifier: string },
	{ dispatch: ThunkAppDispatch; state: RootState; rejectValue: string }
>(
	'quote/syncRecapInfoProv',
	async ({ modifier }, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth, quotes } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			const quoteFilter = getCostlineFetchFilter(userTableInfo)
			const quoteId = quotes?.present.currentQuote?.id
			const quoteType = quotes?.present.currentQuote?.quoteType
			const currentQuoteDetails = quotes?.present.currentQuote
			if (!quoteId || !quoteType || !currentQuoteDetails) {
				return
			}
			const previousRows: ReduxRecapInfo[] = await fetchRecapInfoUtil(
				quoteId,
				quoteFilter
			)
			if (previousRows?.length === 0) {
				return
			}
			const dictPreviousRows = previousRows.reduce(
				(acc, row) => {
					acc[row.id] = row
					return acc
				},
				{} as { [key: string]: ReduxRecapInfo }
			)
			// We copy over the qt, nb and rate to provQT, provNB and provRate respectively
			// If the modifier is syncNewRows we only copy over if provTotal is null
			// else we copy over all rows
			const updateAllCostlinepromises: any[] = []
			const updatedRows = cloneDeep(previousRows)
			updatedRows.map((costline) => {
				if (costline.isCustomCostLine) {
					return costline
				}
				if (modifier === 'syncNewRows') {
					if (
						costline.provisionalTotal === null ||
						costline.provisionalTotal === undefined
					) {
						costline.provisionalQuantity =
							costline.provisionalQuantity ?? costline.quantity
						costline.provisionalNumber =
							costline.provisionalNumber ?? costline.number
						costline.provisionalShootNumber =
							costline.provisionalShootNumber ??
							costline.shootNumber
						costline.provisionalRate =
							costline.provisionalRate ?? costline.rate
						costline.provisionalOtTotal =
							costline.provisionalOtTotal ??
							costline.overtimeHoursCost
					}
				} else {
					costline.provisionalQuantity = costline.quantity
					costline.provisionalNumber = costline.number
					costline.provisionalShootNumber = costline.shootNumber
					costline.provisionalRate = costline.rate
					costline.provisionalOtTotal = costline.overtimeHoursCost
				}
				return costline
			})
			const updatedRecapInfo = getRecapGridTotals({
				localRows: updatedRows,
				quoteType,
				quoteDetails: currentQuoteDetails,
			})
			const baseRecapInfoVariables = ['id', '_version']
			updatedRecapInfo.map((row) => {
				const updatedCostlineValues = getProperUpdateValues({
					updatedInput: row,
					originalInput: dictPreviousRows[row.id],
				})
				if (
					checkNewObjectKeysPresent(
						updatedCostlineValues,
						baseRecapInfoVariables
					)
				) {
					const updatePromise = async () => {
						const {
							data: { updateRecapInfo: apiUpdatedRow },
						} = (await API.graphql(
							graphqlOperation(mutations.updateRecapInfo, {
								input: updatedCostlineValues,
							})
						)) as { data: UpdateRecapInfoMutationApi }

						return apiUpdatedRow
					}
					updateAllCostlinepromises.push(updatePromise())
				}
				return row
			})
			await Promise.allSettled(updateAllCostlinepromises)
			dispatch(fetchRecapQuoteInfo({ quoteId }))
			return
		} catch (error) {
			const errorMessage = getErrorMessage(error)
			makeErrorToast(msg)
			Sentry.captureException(`error syncRecapInfoProv:  ${error}`)
			return rejectWithValue(errorMessage)
		}
	}
)

export const calculateMarginsAndTotals = createAsyncThunk<
	void,
	{ quoteId: string },
	{ rejectValue: string }
>(
	'quote/calculateMarginsAndTotals',
	async ({ quoteId }, { rejectWithValue }) => {
		try {
			await API.graphql(
				graphqlOperation(mutations.calculateMarginsAndTotals, {
					quoteId,
				})
			)
			await API.graphql(
				graphqlOperation(queries.getQuoteTotals, {
					quoteId,
				})
			)
			return
		} catch (error) {
			const errorMessage = getErrorMessage(error)
			makeErrorToast(msg)
			Sentry.captureException(
				`error calculateMarginsAndTotals:  ${error}`
			)
			return rejectWithValue(errorMessage)
		}
	}
)

export const generateAidBidLetter = createAsyncThunk<
	void,
	{ quoteId: string },
	{ dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'quote/generateAidBidLetter',
	async ({ quoteId }, { dispatch, rejectWithValue }) => {
		try {
			await API.graphql(
				graphqlOperation(mutations.generateAiBidLetter, {
					quoteId,
				})
			)
			dispatch(fetchQuote({ quoteId }))
			return
		} catch (error) {
			const errorMessage = getErrorMessage(error)
			makeErrorToast(msg)
			Sentry.captureException(
				`error calculateMarginsAndTotals:  ${error}`
			)
			return rejectWithValue(errorMessage)
		}
	}
)

export const quoteSlice = createSlice({
	name: 'quote',
	initialState,
	reducers: {
		setCurrentQuoteInfo: (state, action) => {
			state.currentQuoteInfo = action.payload
		},
		setSingleRecapInfo: (state, action) => {
			state.singleRecapInfo = action.payload
		},
		setRemoveSupplierFromPrepConfirmDialog: (state, action) => {
			state.removeSupplierFromPrepConfirmDialogStatus =
				action.payload.status
		},
		setRemoveDateRangeFromRecapConfirmDialog: (state, action) => {
			state.removeDateRangeFromRecapConfirmDialogStatus =
				action.payload.status
		},
		setFetchRecapQuoteInfoApiStatus: (state, action) => {
			state.fetchRecapQuoteInfoApiStatus = action.payload.status
		},
		setSummaryFetchQuoteStatus: (state, action) => {
			state.summaryFetchQuoteStatus = action.payload.status
		},
		setAddQuoteModalOpenStatus: (state, action) => {
			state.addQuoteModalOpenStatus = action.payload.status
		},
		setAddQuoteJsonModalOpenStatus: (state, action) => {
			state.addQuoteJsonModalOpenStatus = action.payload.status
			state.quoteJsonDetails = action.payload.quoteJsonDetails
		},
		setAddQuoteFilledCostlineModalOpenStatus: (state, action) => {
			state.addQuoteFilledCostlineModalOpenStatus = action.payload.status
		},
		setQuoteDeleteDialogOpenStatus: (state, action) => {
			state.quoteDeleteDialogOpenStatus = action.payload.status
			state.currentQuote = action.payload.quote
		},
		setUpdateQuoteInfoLoadingStatus: (state, action) => {
			state.updateQuoteInfoApiStatus = action.payload
		},
		setUpdateQuoteInfoDebounceApiStatus: (state, action) => {
			state.updateQuoteInfoDebounceApiStatus = action.payload
		},
		setUpdateRecapQuoteInfoDebounceApiStatus: (state, action) => {
			state.updateRecapQuoteInfoDebounceApiStatus = action.payload
		},
		setUpdateRecapQuoteInfoLoadingStatus: (state, action) => {
			state.updateRecapQuoteInfoApiStatus = action.payload
		},
		setCreateOrUpdateQuoteApiStatus: (state, action) => {
			state.createOrUpdateQuoteApiStatus = action.payload
		},
		clearQuote: (state, action: PayloadAction<void>) => {
			state.quoteInfo = []
			state.recapQuoteInfo = []
			state.currentQuote = null
			state.maximizeMinimize = 'maximize'
			state.scrollToCategory = null
			state.calculateRecapInfoStatus = false
			state.filters = {
				item: '',
				item_fr: '',
			}
		},
		clearQuoteInfo: (state, action: PayloadAction<void>) => {
			state.quoteInfo = []
		},
		clearRecapQuoteInfo: (state, action: PayloadAction<void>) => {
			state.recapQuoteInfo = []
			state.calculateRecapInfoStatus = false
		},
		setQuoteSummaryTotalsModalOpenStatus: (state, action) => {
			state.quoteSummaryTotalsModalOpenStatus = action.payload
		},
		setQuoteUpdateDiscountModalOpenStatus: (state, action) => {
			state.quoteUpdateDiscountModalOpenStatus = action.payload
		},
		setQuoteUpdateCustomizeModalOpenStatus: (state, action) => {
			state.quoteUpdateCustomizeModalOpenStatus = action.payload
		},
		setQuoteShareModalOpenStatus: (state, action) => {
			state.quoteShareModalOpenStatus = action.payload
		},
		setEditCo2EmissionSectionModalOpenStatus: (state, action) => {
			state.editCo2EmissionSectionModalOpenStatus = action.payload
		},
		setEditMarginLabelModalOpenStatus: (state, action) => {
			state.editMarginLabelModalOpenStatus = action.payload
		},
		setRecapSummaryTotalsModalOpenStatus: (state, action) => {
			state.recapSummaryTotalsModalOpenStatus = action.payload
		},
		setScrollToCategoryModalOpenStatus: (state, action) => {
			state.scrollToCategoryModalOpenStatus = action.payload
		},
		setQuotePdfGeneratorModalOpenStatus: (state, action) => {
			state.quotePdfGeneratorModalOpenStatus = action.payload
		},
		setRecapQuotePdfModalOpenStatus: (state, action) => {
			state.recapQuotePdfModalOpenStatus = action.payload
		},
		setQuoteDuplicateDialogOpenStatus: (state, action) => {
			state.quoteDuplicateDialogOpenStatus = action.payload
		},
		setQuoteMoveDialogOpenStatus: (state, action) => {
			state.quoteMoveDialogOpenStatus = action.payload
		},
		setQuoteSmgDialogStatus: (state, action) => {
			state.quoteSmgDialogStatus = action.payload
		},
		setQuoteEditModalStatus: (state, action) => {
			state.quoteEditModalStatus = action.payload
		},
		setMergeQuoteModalOpenStatus: (state, action) => {
			state.mergeQuoteModalOpenStatus = action.payload.status
		},
		setAddSecondaryQuoteModalOpenStatus: (state, action) => {
			state.addSecondaryQuoteModalOpenStatus = action.payload.status
		},
		setCostLineLabelSyncDialogOpenStatus: (state, action) => {
			state.costLineLabelSyncDialogOpenStatus = action.payload.status
		},
		setSyncMealDialogOpenStatus: (state, action) => {
			state.syncMealDialogOpenStatus = action.payload.status
		},
		setScrollToQuoteCategory: (state, action) => {
			state.scrollToCategory = action.payload
		},
		setMinimize: (state) => {
			state.maximizeMinimize = 'minimize'
		},
		setMaximize: (state) => {
			state.maximizeMinimize = 'maximize'
		},
		setMaximizeMinimize: (state) => {
			if (state.maximizeMinimize === 'minimize') {
				state.maximizeMinimize = 'maximize'
			} else {
				state.maximizeMinimize = 'minimize'
			}
		},
		setGridFilters: (state, action) => {
			state.filters = action.payload
		},
		setClearFilters: (state) => {
			state.filters = {
				item: '',
				item_fr: '',
			}
		},
		setGridExpandedRows: (state, action) => {
			state.expandedRows = action.payload
		},
		setCurrentQuote: (state, action) => {
			state.currentQuote = action.payload
		},
		setQuoteInfo: (state, action) => {
			state.quoteInfo = action.payload
		},
		setRecapQuoteInfo: (state, action) => {
			state.recapQuoteInfo = action.payload
		},
		setSmgMenuAnchorEl: (state, action) => {
			state.smgMenuAnchorEl = action.payload
		},
		setEditQuoteMenuAnchorEl: (state, action) => {
			state.editQuoteMenuAnchorEl = action.payload
		},
		updateSingleRecapCostline: (state, action) => {
			const index = action.payload.tableRowIndex
			state.recapQuoteInfo[index] = action.payload
		},
		setAddSecondaryQuoteApiStatus: (state, action) => {
			state.addSecondaryQuoteApiStatus = action.payload
		},
		setEcoProdDialogOpenStatus: (state, action) => {
			state.ecoProdDialogOpenStatus = action.payload
		},
		setShowOtColumns: (state, action) => {
			state.showOtColumns = action.payload
			localStorage.setItem(
				'showOtColumns',
				JSON.stringify(action.payload)
			)
		},
		setColumnVisibility: (state, action) => {
			state.columnVisibility = action.payload.columnVisibility
			// Save to localStorage with quoteId if provided
			if (action.payload.quoteId) {
				const {
					saveColumnVisibilityForQuote,
				} = require('src/utils/columnVisibilityUtils')
				saveColumnVisibilityForQuote(
					action.payload.quoteId,
					action.payload.columnVisibility
				)
			} else {
				// Fallback to old behavior for backward compatibility
				localStorage.setItem(
					'columnVisibility',
					JSON.stringify(action.payload.columnVisibility)
				)
			}
		},
		loadColumnVisibilityForQuote: (state, action) => {
			const {
				getColumnVisibilityForQuote,
			} = require('src/utils/columnVisibilityUtils')
			const quoteId = action.payload
			const columnVisibility = getColumnVisibilityForQuote(quoteId)
			state.columnVisibility = columnVisibility
		},
		setPrepColumnVisibility: (state, action) => {
			state.prepColumnVisibility = action.payload.columnVisibility
			if (action.payload.quoteId) {
				const {
					savePrepColumnVisibilityForQuote,
				} = require('src/utils/columnVisibilityUtils')
				savePrepColumnVisibilityForQuote(
					action.payload.quoteId,
					action.payload.columnVisibility
				)
			}
		},
		loadPrepColumnVisibilityForQuote: (state, action) => {
			const {
				getPrepColumnVisibilityForQuote,
			} = require('src/utils/columnVisibilityUtils')
			const quoteId = action.payload
			state.prepColumnVisibility =
				getPrepColumnVisibilityForQuote(quoteId)
		},
		setWrapColumnVisibility: (state, action) => {
			state.wrapColumnVisibility = action.payload.columnVisibility
			if (action.payload.quoteId) {
				const {
					saveWrapColumnVisibilityForQuote,
				} = require('src/utils/columnVisibilityUtils')
				saveWrapColumnVisibilityForQuote(
					action.payload.quoteId,
					action.payload.columnVisibility
				)
			}
		},
		loadWrapColumnVisibilityForQuote: (state, action) => {
			const {
				getWrapColumnVisibilityForQuote,
			} = require('src/utils/columnVisibilityUtils')
			const quoteId = action.payload
			state.wrapColumnVisibility =
				getWrapColumnVisibilityForQuote(quoteId)
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchAllQuotes.pending, (state, action) => {
			state.fetchAllQuotesApiStatus = 'loading'
		})
		builder.addCase(fetchAllQuotes.fulfilled, (state, action) => {
			state.fetchAllQuotesApiStatus = 'succeeded'
			state.allQuotes = action.payload
		})
		builder.addCase(fetchAllQuotes.rejected, (state, action) => {
			state.fetchAllQuotesApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(fetchQuote.pending, (state, action) => {
			state.fetchQuoteApiStatus = 'loading'
			if (action.meta.arg.page === 'quoteCard') {
				state.summaryFetchQuoteStatus = 'loading'
			}
		})
		builder.addCase(fetchQuote.fulfilled, (state, action) => {
			state.fetchQuoteApiStatus = 'succeeded'
			if (
				(action.payload.maximizeMinimize === 'maximize' ||
					action.payload.maximizeMinimize === 'minimize') &&
				action.meta.arg.status === 'pageLoad'
			) {
				state.maximizeMinimize = action.payload.maximizeMinimize
			}
			state.currentQuote = action.payload
		})
		builder.addCase(fetchQuote.rejected, (state, action) => {
			state.fetchQuoteApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(fetchQuoteInfo.pending, (state, action) => {
			state.fetchQuoteInfoApiStatus = 'loading'
		})
		builder.addCase(fetchQuoteInfo.fulfilled, (state, action) => {
			state.fetchQuoteInfoApiStatus = 'succeeded'
			state.quoteInfo = action.payload.quoteInfo
			state.oldQuoteInfo = action.payload.quoteInfo
			state.sectionRenameDict = action.payload.sectionRenameDict
			state.updateQuoteInfoApiStatus = 'succeeded'
			if (action.meta.arg.page === 'quoteCard') {
				state.summaryFetchQuoteStatus = 'succeeded'
			}
		})
		builder.addCase(fetchQuoteInfo.rejected, (state, action) => {
			state.fetchQuoteInfoApiStatus = 'failed'
			state.error = action.error.message
			state.updateQuoteInfoApiStatus = 'failed'
		})
		builder.addCase(fetchRecapQuoteInfo.pending, (state, action) => {
			state.fetchRecapQuoteInfoApiStatus = 'loading'
		})
		builder.addCase(fetchRecapQuoteInfo.fulfilled, (state, action) => {
			state.fetchRecapQuoteInfoApiStatus = 'succeeded'
			state.updateRecapQuoteInfoApiStatus = 'succeeded'
			if (action.payload) {
				state.recapQuoteInfo = action.payload
				state.oldRecapQuoteInfo = action.payload
			}
			if (action.meta.arg.calculateRecapInfo) {
				state.calculateRecapInfoStatus = true
			}
		})
		builder.addCase(fetchRecapQuoteInfo.rejected, (state, action) => {
			state.fetchRecapQuoteInfoApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(createQuote.pending, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'loading'
		})
		builder.addCase(createQuote.fulfilled, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'succeeded'
			state.addQuoteModalOpenStatus = false
			state.addQuoteJsonModalOpenStatus = false
			state.quoteJsonDetails = null
		})
		builder.addCase(createQuote.rejected, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'failed'
			state.addQuoteModalOpenStatus = false
			state.addQuoteJsonModalOpenStatus = false
			state.quoteJsonDetails = null
			state.error = action.error?.message
			if (action.payload) {
				state.error = action.payload
			}
		})
		builder.addCase(createQuoteFromJson.pending, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'loading'
		})
		builder.addCase(createQuoteFromJson.fulfilled, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'succeeded'
			state.addQuoteModalOpenStatus = false
			state.addQuoteJsonModalOpenStatus = false
			state.quoteJsonDetails = null
		})
		builder.addCase(createQuoteFromJson.rejected, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'failed'
			state.addQuoteModalOpenStatus = false
			state.addQuoteJsonModalOpenStatus = false
			state.quoteJsonDetails = null
			state.error = action.error?.message
			if (action.payload) {
				state.error = action.payload
			}
		})
		builder.addCase(createQuoteFromTemplate.pending, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'loading'
		})
		builder.addCase(createQuoteFromTemplate.fulfilled, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'succeeded'
			state.addQuoteModalOpenStatus = false
		})
		builder.addCase(createQuoteFromTemplate.rejected, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'failed'
			state.addQuoteModalOpenStatus = false
			state.error = action.error?.message
			if (action.payload) {
				state.error = action.payload
			}
		})
		builder.addCase(updateQuote.pending, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'loading'
		})
		builder.addCase(updateQuote.fulfilled, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'succeeded'
			if (action.meta.arg.modifier !== 'keepEditModalOpen') {
				state.quoteEditModalStatus = { isOpen: false }
			}
			if (action.meta.arg.modifier === 'closeUpdateModals') {
				state.quoteUpdateDiscountModalOpenStatus = false
				state.quoteUpdateCustomizeModalOpenStatus = false
			}
		})
		builder.addCase(updateQuote.rejected, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'rejected'
			if (action.meta.arg.modifier !== 'keepEditModalOpen') {
				state.quoteEditModalStatus = { isOpen: false }
			}
			state.error = action.error?.message
		})
		builder.addCase(updateQuoteOnBlur.pending, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'loading'
		})
		builder.addCase(updateQuoteOnBlur.fulfilled, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'succeeded'
			state.currentQuote = action.payload
			if (state.allQuotes?.length > 0) {
				const newQuote = action.payload
				state.allQuotes = state.allQuotes.map((quote) => {
					if (quote.id === newQuote.id) {
						return {
							...quote,
							...(newQuote as UpdateQuoteAttributes),
						}
					}
					return quote
				})
			}
		})
		builder.addCase(updateQuoteOnBlur.rejected, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'rejected'
			state.error = action.error?.message
		})
		builder.addCase(toggleQuoteOffline.pending, (state, action) => {
			state.updateOfflineIdsStatus = 'loading'
		})
		builder.addCase(toggleQuoteOffline.fulfilled, (state, action) => {
			state.updateOfflineIdsStatus = 'succeeded'
		})
		builder.addCase(toggleQuoteOffline.rejected, (state, action) => {
			state.updateOfflineIdsStatus = 'rejected'
			state.error = action.error?.message
		})
		builder.addCase(updateQuoteInfo.pending, (state, action) => {
			++state.updateQuoteInfoApiCounter
			state.updateQuoteInfoDebounceApiStatus = 'loading'
		})
		builder.addCase(updateQuoteInfo.fulfilled, (state, action) => {
			state.oldQuoteInfo = action.payload
			--state.updateQuoteInfoApiCounter
			state.updateQuoteInfoDebounceApiStatus = 'succeeded'
		})
		builder.addCase(updateQuoteInfo.rejected, (state, action) => {
			if (
				typeof action.payload === 'object' &&
				action.payload?.oldQuoteInfo
			) {
				state.oldQuoteInfo = action.payload.oldQuoteInfo
			} else {
				state.error = action.error.message
				state.quoteInfo = [...state.oldQuoteInfo]
			}
			--state.updateQuoteInfoApiCounter
			state.updateQuoteInfoDebounceApiStatus = 'rejected'
		})
		builder.addCase(addQuoteInfoCostline.rejected, (state, action) => {
			state.error = action.error.message
			state.quoteInfo = [...state.oldQuoteInfo]
			state.updateQuoteInfoApiStatus = 'rejected'
		})
		builder.addCase(addQuoteInfoCostline2.fulfilled, (state, action) => {
			state.quoteInfo = action.payload
			state.oldQuoteInfo = action.payload
			state.updateQuoteInfoApiStatus = 'succeeded'
		})
		builder.addCase(addQuoteInfoCostline2.rejected, (state, action) => {
			state.error = action.error.message
			state.quoteInfo = [...state.oldQuoteInfo]
			state.updateQuoteInfoApiStatus = 'rejected'
		})
		builder.addCase(deleteQuoteInfo.fulfilled, (state, action) => {
			state.quoteInfo = action.payload
			state.oldQuoteInfo = action.payload
			state.updateQuoteInfoApiStatus = 'succeeded'
		})
		builder.addCase(deleteQuoteInfo.rejected, (state, action) => {
			state.error = action.error.message
			state.quoteInfo = [...state.oldQuoteInfo]
			state.updateQuoteInfoApiStatus = 'rejected'
		})
		builder.addCase(
			addRecapQuoteInfoCostline.fulfilled,
			(state, action) => {
				state.oldRecapQuoteInfo = [...action.payload]
				state.recapQuoteInfo = [...action.payload]
				state.updateRecapQuoteInfoApiStatus = 'succeeded'
			}
		)

		builder.addCase(addRecapQuoteInfoCostline.rejected, (state, action) => {
			state.error = action.error.message
			state.recapQuoteInfo = [...state.oldRecapQuoteInfo]
			state.updateRecapQuoteInfoApiStatus = 'rejected'
		})
		builder.addCase(
			addRecapQuoteInfoCostline2.fulfilled,
			(state, action) => {
				state.recapQuoteInfo = action.payload
				state.oldRecapQuoteInfo = action.payload
				state.updateRecapQuoteInfoApiStatus = 'succeeded'
			}
		)
		builder.addCase(
			addRecapQuoteInfoCostline2.rejected,
			(state, action) => {
				state.error = action.error.message
				state.recapQuoteInfo = [...state.oldRecapQuoteInfo]
				state.updateRecapQuoteInfoApiStatus = 'rejected'
			}
		)
		builder.addCase(updateRecapQuoteInfo.pending, (state, action) => {
			++state.updateRecapQuoteInfoApiCounter
			state.updateRecapQuoteInfoDebounceApiStatus = 'loading'
		})
		builder.addCase(updateRecapQuoteInfo.fulfilled, (state, action) => {
			state.oldRecapQuoteInfo = [...action.payload]
			state.updateRecapQuoteInfoDebounceApiStatus = 'succeeded'
			--state.updateRecapQuoteInfoApiCounter
			state.updateRecapQuoteInfoApiStatus = 'succeeded'
		})
		builder.addCase(updateRecapQuoteInfo.rejected, (state, action) => {
			if (
				typeof action.payload === 'object' &&
				action.payload?.oldRecapQuoteInfo
			) {
				state.oldRecapQuoteInfo = action.payload.oldRecapQuoteInfo
			} else {
				state.error = action.error.message
				state.recapQuoteInfo = [...state.oldRecapQuoteInfo]
			}
			state.updateRecapQuoteInfoDebounceApiStatus = 'rejected'
			--state.updateRecapQuoteInfoApiCounter
			state.updateRecapQuoteInfoApiStatus = 'rejected'
		})
		builder.addCase(deleteQuote.pending, (state, action) => {
			state.deleteQuoteApiStatus = 'loading'
		})
		builder.addCase(deleteQuote.fulfilled, (state, action) => {
			state.deleteQuoteApiStatus = 'succeeded'
			state.quoteDeleteDialogOpenStatus = false
		})
		builder.addCase(deleteQuote.rejected, (state, action) => {
			state.deleteQuoteApiStatus = 'failed'
			state.quoteDeleteDialogOpenStatus = false
			state.error = action.error.message
		})
		builder.addCase(refreshRecapQuoteInfo.pending, (state, action) => {
			state.refreshRecapQuoteInfoApiStatus = 'loading'
		})
		builder.addCase(refreshRecapQuoteInfo.fulfilled, (state, action) => {
			state.refreshRecapQuoteInfoApiStatus = 'succeeded'
		})
		builder.addCase(refreshRecapQuoteInfo.rejected, (state, action) => {
			state.refreshRecapQuoteInfoApiStatus = 'rejected'
		})
		builder.addCase(syncRecapInfoProv.pending, (state, action) => {
			state.refreshRecapQuoteInfoApiStatus = 'loading'
		})
		builder.addCase(syncRecapInfoProv.fulfilled, (state, action) => {
			state.refreshRecapQuoteInfoApiStatus = 'succeeded'
		})
		builder.addCase(syncRecapInfoProv.rejected, (state, action) => {
			state.refreshRecapQuoteInfoApiStatus = 'rejected'
		})
		builder.addCase(duplicateQuote.pending, (state, action) => {
			state.duplicateQuoteApiStatus = 'loading'
		})
		builder.addCase(duplicateQuote.fulfilled, (state, action) => {
			state.duplicateQuoteApiStatus = 'succeeded'
			state.quoteDuplicateDialogOpenStatus = false
		})
		builder.addCase(duplicateQuote.rejected, (state, action) => {
			state.duplicateQuoteApiStatus = 'rejected'
			state.quoteDuplicateDialogOpenStatus = false
			state.error = action.error?.message
			if (action.payload) {
				state.error = action.payload
			}
		})
		builder.addCase(moveQuote.pending, (state, action) => {
			state.moveQuoteApiStatus = 'loading'
		})
		builder.addCase(moveQuote.fulfilled, (state, action) => {
			state.moveQuoteApiStatus = 'succeeded'
			state.quoteMoveDialogOpenStatus = false
		})
		builder.addCase(moveQuote.rejected, (state, action) => {
			state.moveQuoteApiStatus = 'rejected'
			state.quoteMoveDialogOpenStatus = false
			state.error = action.error?.message
			if (action.payload) {
				state.error = action.payload
			}
		})
		builder.addCase(removeQuoteSmg.pending, (state, action) => {
			state.addOrRemoveSmgQuoteApiStatus = 'loading'
		})
		builder.addCase(removeQuoteSmg.fulfilled, (state, action) => {
			state.addOrRemoveSmgQuoteApiStatus = 'succeeded'
			state.quoteSmgDialogStatus = { isOpen: false, section: null }
		})
		builder.addCase(removeQuoteSmg.rejected, (state, action) => {
			state.addOrRemoveSmgQuoteApiStatus = 'rejected'
			state.quoteSmgDialogStatus = { isOpen: false, section: null }
			state.error = action.error?.message
		})
		builder.addCase(addQuoteSmg.pending, (state, action) => {
			state.addOrRemoveSmgQuoteApiStatus = 'loading'
		})
		builder.addCase(addQuoteSmg.fulfilled, (state, action) => {
			state.addOrRemoveSmgQuoteApiStatus = 'succeeded'
			state.quoteSmgDialogStatus = { isOpen: false, section: null }
		})
		builder.addCase(addQuoteSmg.rejected, (state, action) => {
			state.addOrRemoveSmgQuoteApiStatus = 'rejected'
			state.quoteSmgDialogStatus = { isOpen: false, section: null }
			state.error = action.error?.message
		})
		builder.addCase(uploadQuote.pending, (state, action) => {
			state.uploadQuoteApiStatus = 'loading'
		})
		builder.addCase(uploadQuote.fulfilled, (state, action) => {
			state.uploadQuoteApiStatus = 'succeeded'
			state.quoteMoveDialogOpenStatus = false
		})
		builder.addCase(uploadQuote.rejected, (state, action) => {
			state.uploadQuoteApiStatus = 'rejected'
			state.quoteMoveDialogOpenStatus = false
			state.error = action.error?.message
			if (action.payload) {
				state.error = action.payload
			}
		})
		builder.addCase(removeSupplierFromPrep.pending, (state, action) => {
			state.updateRecapQuoteInfoApiStatus = 'loading'
			state.removeSupplierFromPrepApiStatus = 'loading'
		})
		builder.addCase(removeSupplierFromPrep.fulfilled, (state, action) => {
			state.updateRecapQuoteInfoApiStatus = 'succeeded'
			state.removeSupplierFromPrepApiStatus = 'succeeded'
			state.removeSupplierFromPrepConfirmDialogStatus = false
		})
		builder.addCase(removeSupplierFromPrep.rejected, (state, action) => {
			state.updateRecapQuoteInfoApiStatus = 'rejected'
			state.removeSupplierFromPrepApiStatus = 'rejected'
		})
		builder.addCase(removeDateRangeFromRecap.pending, (state, action) => {
			state.removeSupplierFromPrepApiStatus = 'loading'
		})
		builder.addCase(removeDateRangeFromRecap.fulfilled, (state, action) => {
			state.removeSupplierFromPrepApiStatus = 'succeeded'
			state.removeDateRangeFromRecapConfirmDialogStatus = false
		})
		builder.addCase(removeDateRangeFromRecap.rejected, (state, action) => {
			state.removeSupplierFromPrepApiStatus = 'rejected'
		})
		builder.addCase(mergeQuote.pending, (state, action) => {
			state.mergeQuoteApiStatus = 'loading'
		})
		builder.addCase(mergeQuote.fulfilled, (state, action) => {
			state.mergeQuoteApiStatus = 'succeeded'
			state.mergeQuoteModalOpenStatus = false
		})
		builder.addCase(mergeQuote.rejected, (state, action) => {
			state.mergeQuoteApiStatus = 'rejected'
		})
		builder.addCase(fetchMergedQuoteList.pending, (state, action) => {
			state.listMergeQuoteApiStatus = 'loading'
		})
		builder.addCase(fetchMergedQuoteList.fulfilled, (state, action) => {
			state.listMergeQuoteApiStatus = 'succeeded'
			state.listMergedQuotesData = [...action.payload]
		})
		builder.addCase(fetchMergedQuoteList.rejected, (state, action) => {
			state.listMergeQuoteApiStatus = 'rejected'
		})
		builder.addCase(assignUserToQuote.pending, (state, action) => {
			state.assignUserToQuoteApiStatus = 'loading'
		})
		builder.addCase(assignUserToQuote.fulfilled, (state, action) => {
			state.assignUserToQuoteApiStatus = 'succeeded'
			state.currentQuote = null
		})
		builder.addCase(assignUserToQuote.rejected, (state, action) => {
			state.assignUserToQuoteApiStatus = 'rejected'
		})
		builder.addCase(removeAssignUserToQuote.pending, (state, action) => {
			state.removeAssignUserToQuoteApiStatus = 'loading'
		})
		builder.addCase(removeAssignUserToQuote.fulfilled, (state, action) => {
			state.removeAssignUserToQuoteApiStatus = 'succeeded'
			state.currentQuote = null
		})
		builder.addCase(removeAssignUserToQuote.rejected, (state, action) => {
			state.removeAssignUserToQuoteApiStatus = 'rejected'
		})
		builder.addCase(toggleSetQuoteExpiry.pending, (state, action) => {
			state.toggleSetQuoteExpiryApiStatus = 'loading'
		})
		builder.addCase(toggleSetQuoteExpiry.fulfilled, (state, action) => {
			state.toggleSetQuoteExpiryApiStatus = 'succeeded'
		})
		builder.addCase(toggleSetQuoteExpiry.rejected, (state, action) => {
			state.toggleSetQuoteExpiryApiStatus = 'rejected'
		})
		builder.addCase(addSecondaryToMasterQuote.pending, (state, action) => {
			state.addSecondaryQuoteApiStatus = 'loading'
		})
		builder.addCase(
			detachMemberQuoteFromMasterQuote.pending,
			(state, action) => {
				state.detachMemberQuoteApiStatus = 'loading'
			}
		)
		builder.addCase(
			addFractionalSortKeyToQuote.pending,
			(state, action) => {
				state.updateCostLineLabelApiStatus = 'loading'
			}
		)
		builder.addCase(updateCostLineLabel.pending, (state, action) => {
			state.updateCostLineLabelApiStatus = 'loading'
		})
		builder.addCase(updateCostLineLabel.fulfilled, (state, action) => {
			state.updateCostLineLabelApiStatus = 'succeeded'
			state.costLineLabelSyncDialogOpenStatus = false
		})
		builder.addCase(updateCostLineLabel.rejected, (state, action) => {
			state.updateCostLineLabelApiStatus = 'rejected'
			state.error = action.error.message
			state.costLineLabelSyncDialogOpenStatus = false
		})
		builder.addCase(
			addSecondaryToMasterQuote.fulfilled,
			(state, action) => {
				state.addSecondaryQuoteApiStatus = 'succeeded'
				state.addSecondaryQuoteModalOpenStatus = false
			}
		)
		builder.addCase(addSecondaryToMasterQuote.rejected, (state, action) => {
			state.addSecondaryQuoteApiStatus = 'rejected'
		})
		builder.addCase(
			detachMemberQuoteFromMasterQuote.fulfilled,
			(state, action) => {
				state.detachMemberQuoteApiStatus = 'succeeded'
			}
		)
		builder.addCase(
			detachMemberQuoteFromMasterQuote.rejected,
			(state, action) => {
				state.detachMemberQuoteApiStatus = 'rejected'
			}
		)
		builder.addCase(generateAidBidLetter.pending, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'loading'
		})
		builder.addCase(generateAidBidLetter.fulfilled, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'succeeded'
		})
		builder.addCase(generateAidBidLetter.rejected, (state, action) => {
			state.createOrUpdateQuoteApiStatus = 'rejected'
			state.error = action.error?.message
		})
		builder.addCase(
			createQuoteWithCostlinesFilled.pending,
			(state, action) => {
				state.createOrUpdateQuoteApiStatus = 'loading'
			}
		)
		builder.addCase(
			createQuoteWithCostlinesFilled.fulfilled,
			(state, action) => {
				state.createOrUpdateQuoteApiStatus = 'succeeded'
				state.addQuoteFilledCostlineModalOpenStatus = false
			}
		)
		builder.addCase(
			createQuoteWithCostlinesFilled.rejected,
			(state, action) => {
				state.createOrUpdateQuoteApiStatus = 'rejected'
				state.error = action.error?.message
			}
		)
		builder.addCase(calculateMarginsAndTotals.pending, (state, action) => {
			state.calculateMarginsAndTotalsApiStatus = 'loading'
		})
		builder.addCase(
			calculateMarginsAndTotals.fulfilled,
			(state, action) => {
				state.calculateMarginsAndTotalsApiStatus = 'succeeded'
			}
		)
		builder.addCase(calculateMarginsAndTotals.rejected, (state, action) => {
			state.calculateMarginsAndTotalsApiStatus = 'failed'
			state.error = action.error?.message
		})
		builder.addMatcher(
			quoteApi.endpoints.fetchQuoteById.matchFulfilled,
			(state, action) => {
				state.currentQuote = action.payload
			}
		)
		builder.addMatcher(
			quoteInfoApi.endpoints.fetchQuoteInfoByQuoteId.matchFulfilled,
			(state, action) => {
				state.quoteInfo = action.payload
				state.oldQuoteInfo = action.payload
			}
		)
		builder.addMatcher(
			recapInfoApi.endpoints.fetchRecapInfoByQuoteId.matchFulfilled,
			(state, action) => {
				state.recapQuoteInfo = action.payload
				state.oldRecapQuoteInfo = action.payload
			}
		)
		builder.addMatcher(
			quoteApi.endpoints.createSectionRename.matchFulfilled,
			(state, action) => {
				state.updateQuoteInfoApiStatus = 'succeeded'
			}
		)
		builder.addMatcher(
			quoteApi.endpoints.createSectionRename.matchRejected,
			(state, action) => {
				state.updateQuoteInfoApiStatus = 'rejected'
			}
		)
		builder.addMatcher(
			quoteApi.endpoints.updateSectionRename.matchFulfilled,
			(state, action) => {
				state.updateQuoteInfoApiStatus = 'succeeded'
			}
		)
		builder.addMatcher(
			quoteApi.endpoints.updateSectionRename.matchRejected,
			(state, action) => {
				state.updateQuoteInfoApiStatus = 'rejected'
				state.error = action.error?.message
			}
		)
		builder.addMatcher(
			quoteApi.endpoints.fetchSectionRename.matchFulfilled,
			(state, action) => {
				state.sectionRenameDict = action.payload
			}
		)
	},
})

export const {
	setCurrentQuoteInfo,
	setSingleRecapInfo,
	setRemoveSupplierFromPrepConfirmDialog,
	setRemoveDateRangeFromRecapConfirmDialog,
	setFetchRecapQuoteInfoApiStatus,
	setSummaryFetchQuoteStatus,
	setAddQuoteModalOpenStatus,
	setAddQuoteJsonModalOpenStatus,
	setQuoteDeleteDialogOpenStatus,
	setUpdateQuoteInfoLoadingStatus,
	setUpdateQuoteInfoDebounceApiStatus,
	setUpdateRecapQuoteInfoDebounceApiStatus,
	setUpdateRecapQuoteInfoLoadingStatus,
	clearQuote,
	clearQuoteInfo,
	clearRecapQuoteInfo,
	setQuoteSummaryTotalsModalOpenStatus,
	setQuoteUpdateDiscountModalOpenStatus,
	setQuoteUpdateCustomizeModalOpenStatus,
	setQuoteShareModalOpenStatus,
	setEditCo2EmissionSectionModalOpenStatus,
	setEditMarginLabelModalOpenStatus,
	setRecapSummaryTotalsModalOpenStatus,
	setScrollToCategoryModalOpenStatus,
	setQuotePdfGeneratorModalOpenStatus,
	setRecapQuotePdfModalOpenStatus,
	setQuoteDuplicateDialogOpenStatus,
	setQuoteMoveDialogOpenStatus,
	setQuoteSmgDialogStatus,
	setQuoteEditModalStatus,
	setMergeQuoteModalOpenStatus,
	setAddSecondaryQuoteModalOpenStatus,
	setCostLineLabelSyncDialogOpenStatus,
	setSyncMealDialogOpenStatus,
	setScrollToQuoteCategory,
	setMinimize,
	setMaximize,
	setMaximizeMinimize,
	setGridFilters,
	setClearFilters,
	setGridExpandedRows,
	setCurrentQuote,
	setQuoteInfo,
	setRecapQuoteInfo,
	setSmgMenuAnchorEl,
	setEditQuoteMenuAnchorEl,
	updateSingleRecapCostline,
	setAddSecondaryQuoteApiStatus,
	setEcoProdDialogOpenStatus,
	setCreateOrUpdateQuoteApiStatus,
	setAddQuoteFilledCostlineModalOpenStatus,
	setShowOtColumns,
	setColumnVisibility,
	loadColumnVisibilityForQuote,
	setPrepColumnVisibility,
	loadPrepColumnVisibilityForQuote,
	setWrapColumnVisibility,
	loadWrapColumnVisibilityForQuote,
} = quoteSlice.actions

export default quoteSlice.reducer
// export default quoteSlice.reducer as Reducer<QuoteState>
